// src/pages/PatientDetailPage.jsx
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import {
  fetchSheetPatients,
  updateSheetPatient,
  updateSheetPatientMrn,
  SheetWriteRefusedError,
} from "../services/sheetPatientsService";
import { loadDuplicateCandidates } from "../services/regxPatientCreate";
import { mrnChanged, otherPatientsWithMrn, mrnChangeAudit } from "../services/mrnEdit";
import { authedFetch } from "../services/authedFetch";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { db, storage, auth } from "../firebase";
import {
  logPatientAuditEvent,
  buildPatientDiff,
  getMrnLast4,
  PATIENT_PROFILE_FIELDS,
  PATIENT_CLINICAL_FIELDS,
} from "../services/patientAuditService";
import PatientAuditTimeline from "./PatientAuditTimeline";
import PatientNotesTimeline from "./PatientNotesTimeline";
import { fetchActiveCoordinators } from "../services/coordinatorService";
import StatusPicker from "./StatusPicker";
import { DIAGNOSIS_OPTIONS, diagnosisLabel, statusLabel } from "../constants/diseaseOptions";
import { hasConfirmedScreening } from "../services/screeningVisit";
import ScheduleVisitModal from "./ScheduleVisitModal";
import EnrollStudyPickerModal from "./EnrollStudyPickerModal";
import { isMergedStudyCopy } from "../services/studyCopyMerge";
import {
  createEnrollmentRequests,
  cancelEnrollmentRequests,
} from "../services/enrollmentRequestService";
import {
  normalizeEnrolledStudies,
  activeEnrolledStudies,
  mergeEnrolledStudies,
  coordinatorPickEntries,
  mergeEntryUpdates,
  stampReturn,
  diffEnrolledStudies,
  splitCurrentAndPast,
  shouldReturnToReferrals,
  diagnosticsBadgeFor,
  unverifiedLowMatches,
  isDiagnosticsStudy,
  isEnrolledStatus,
  hasUnverifiedScore,
  studyDisplayName,
  entryKey,
  RETURN_REASON_TEXT,
  REFERRALS_STATUS,
} from "../services/enrollmentRules";
import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import {
  getConsentsPath,
  getDuplicatesPath,
  getStudiesPath,
  getStudiesCopyPath,
  getStudyPatientsPath,
  getStudyPotentialPatientsPath,
} from "../services/firestorePaths";
import { activeSiteUsesLegacySheet } from "../services/activeTenant";
import { PFT_HISTORY_ENABLED, PFT_SURVEY_TEXT_ENABLED, UI_REVAMP_ENABLED } from "../config/featureFlags";
import PatientSurveyPanel, { usePatientSurvey } from "./patient/PatientSurveyPanel";
import {
  mergeSurveyChanges,
  mergeSurveyField,
  patientSurveyStore,
  surveyFieldsOfRecord,
  surveyPanelView,
  surveySubmissionIdsOfRecord,
} from "../services/patientSurvey";
import useUnsavedChanges from "../hooks/useUnsavedChanges";
import PftHistoryEditor from "./patient/PftHistoryEditor";
import AddressSoftCheck, { addressChecksOf } from "./patient/AddressSoftCheck";
import { PFT_FLAT_KEYS, needsTestList, pftFieldsForSave, testsFromRecord } from "../services/pftTests";
import {
  NODULE_CHANGES,
  SCAN_TYPES,
  blankScan,
  scanFieldsForSave,
  scansForEditing,
  showNoduleDetails,
  showPriorSize,
} from "../services/diagnosticScans";
import { unverifiedSoftChecks, verificationChanges } from "../services/pftSoftChecks";
import { SMOKING_OPTIONS } from "../services/smoking";
import { useTenant } from "../context/TenantContext";
import { effectiveRole } from "../services/membershipService";
import {
  markPatientPresence,
  clearPatientPresence,
  subscribeToOtherViewers,
  PRESENCE_HEARTBEAT_MS,
} from "../services/patientPresenceService";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { motion, AnimatePresence } from "framer-motion";
import { fadeRise } from "./motionPresets";
import { useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  X,
  ChevronDown,
  Users,
  Phone,
  Mail,
  Calendar,
  IdCard,
  MapPin,
  UserCircle2,
  HeartPulse,
  Activity,
  ClipboardList,
  CheckCircle2,
  AlertTriangle,
  Flame,
  Upload,
  Paperclip,
  FileText,
  Stethoscope,
  Search,
  ExternalLink,
  Sparkles,
  Loader2,
  Plus,
  Wind,
  Droplet,
  Cigarette,
  Moon,
  AirVent,
  Hourglass,
  Maximize2,
  ScanLine,
} from "lucide-react";
import NotesViewerModal from "./patient/NotesViewerModal";


const bannerVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
};

const Chip = ({ children, tone = "default" }) => {
  const map = {
    default: "bg-white/[0.05] text-slate-200 border-white/10",
    success: "bg-emerald-400/15 text-emerald-200 border-emerald-400/30",
    warn: "bg-amber-400/15 text-amber-200 border-amber-400/30",
    info: "bg-purple-400/15 text-purple-200 border-purple-400/30",
    danger: "bg-rose-400/15 text-rose-200 border-rose-400/30",
    yellow: "bg-yellow-400/15 text-yellow-200 border-yellow-400/30",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border backdrop-blur-md ${map[tone]}`}
    >
      {children}
    </span>
  );
};

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

// Google Apps Script endpoint that uploads a patient file into the shared Drive
// folder. Only reachable on the legacy-sheet path, which staging disables — but
// env-driven anyway so a staging build can never write a test file into the
// production Drive. Falls back to the prod endpoint when unset, so production is
// unchanged.
const DRIVE_UPLOAD_URL =
  process.env.REACT_APP_DRIVE_UPLOAD_API ||
  "https://script.google.com/macros/s/AKfycbwFpPP1NfhXdKGeMsdYwRKrptYr5pU5qYHDhcIWu5icB0HnWKWfuvPTesljFmDHosbN/exec";

const STATUS_TONE = {
  enrolled: "success",
  "screen failed": "warn",
  "patient scheduled for screen": "info",
  "ready to schedule": "yellow",
  "potential duplicate": "danger",
  "unable to reach": "danger",
};

const statusTone = (s) => STATUS_TONE[(s || "").toLowerCase().trim()] || "default";

// Reads the "Enrolled in Diagnostics" flag off a loaded record (Firestore doc or
// Sheet row, where it rides inside CLINICAL_DATA). `present` records whether the
// record carried the field at all, so saves only write it when it's in use.
const readDiagnosticsFlag = (p) => {
  const raw = p ? p.enrolledInDiagnostics : undefined;
  const present = raw !== undefined && raw !== null && raw !== "";
  return {
    present,
    value: raw === true || String(raw).toLowerCase() === "true",
    at: (p && p.enrolledInDiagnosticsAt) || null,
    by: (p && p.enrolledInDiagnosticsBy) || null,
  };
};

/* -------- Section nav can expand a section before scrolling to it -------- */
const SectionExpandContext = createContext(null);

/* -------- Collapsible glass section -------- */
const Section = ({
  id,
  title,
  subtitle,
  icon,
  accent = "from-purple-400 to-blue-400",
  defaultOpen = true,
  children,
  headerExtra,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const registerSectionOpen = useContext(SectionExpandContext);

  useEffect(() => {
    if (!registerSectionOpen || !id) return undefined;
    return registerSectionOpen(id, setOpen);
  }, [registerSectionOpen, id]);

  return (
    <motion.section
      id={id}
      variants={fadeRise}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-40px" }}
      className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden scroll-mt-[120px]"
    >
      <div className={`absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r ${accent} opacity-70 pointer-events-none`} />
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.04] via-transparent to-blue-500/[0.04] pointer-events-none" />
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative w-full flex items-center gap-2 px-4 py-3 text-left group"
        aria-expanded={open}
      >
        <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center group-hover:border-purple-300/30 transition shrink-0">
          {icon}
        </div>
        <div className="min-w-0">
          <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white leading-tight">
            {title}
          </h2>
          {subtitle && (
            <p className="text-[10px] text-slate-400 leading-tight mt-0.5 truncate">
              {subtitle}
            </p>
          )}
        </div>
        <span className="ml-2 h-px flex-1 bg-white/5" />
        {headerExtra}
        <span className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center text-slate-300 group-hover:text-white transition shrink-0">
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform duration-300 ${
              open ? "rotate-180" : ""
            }`}
          />
        </span>
      </button>
      {open && <div className="relative px-4 pb-4">{children}</div>}
    </motion.section>
  );
};

/* -------- Input atoms (dark glass) -------- */
const inputCls =
  "w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition";
const inputDateCls = `${inputCls} [color-scheme:dark]`;
const labelCls =
  "block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1";
const selectCls =
  "w-full appearance-none px-3 pr-9 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 cursor-pointer transition";

const NaToggle = ({ checked, onChange, label = "N/A" }) => (
  <label className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.06] cursor-pointer transition text-xs text-slate-300">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-3.5 h-3.5 accent-purple-500"
    />
    {label}
  </label>
);

const PatientDetailPage = () => {

const [isUrgent, setIsUrgent] = useState(false);
const [urgentReason, setUrgentReason] = useState("");
const [urgentTimestamp, setUrgentTimestamp] = useState(null);
const [onHold, setOnHold] = useState(false);
// "Enrolled in Diagnostics": set via the pop-up when a user picks Enrolled and
// answers Yes. The patient then goes back to Lung Center Referrals so they can
// be considered for other studies; the flag stays until a user clears it.
const [enrolledInDiagnostics, setEnrolledInDiagnostics] = useState(false);
const [enrolledInDiagnosticsAt, setEnrolledInDiagnosticsAt] = useState(null);
const [enrolledInDiagnosticsBy, setEnrolledInDiagnosticsBy] = useState(null);
// Whether the loaded record already carried the flag fields. Records that never
// had them are saved without them, so an ordinary save adds no new fields.
const diagFlagLoadedRef = useRef({ present: false, value: false });
// Which studies the patient is enrolled in (see services/enrollmentRules.js).
// Picked in the Enrolled pop-up; saved as `enrolledStudies` on the Firestore
// doc or inside CLINICAL_DATA for Sheet patients. `enrollLoadedRef` holds the
// list as loaded, for the audit diff, and whether the record carried the field
// at all, so records that never used it are saved without it.
const [studyEnrollments, setStudyEnrollments] = useState([]);
const enrollLoadedRef = useRef({ present: false, list: [] });
const loadedStatusRef = useRef(null);
const [showEnrollPicker, setShowEnrollPicker] = useState(false);
const [studyDocs, setStudyDocs] = useState([]);
// Sheet-only: the status + movedToCoordinatorAt as loaded, so handleSave can
// detect a box change the same way the Firestore branch does via a fresh
// getDoc (the Sheet has no per-save fetch to diff against).
const originalStatusRef = useRef(null);
const [sheetMovedToCoordinatorAt, setSheetMovedToCoordinatorAt] = useState(null);
// The MRN as loaded (both stores). A Sheet MRN edit is written only if the row
// still holds this value; it's also what the duplicate-MRN warning compares.
const originalMrnRef = useRef("");
// Admin MRN edit: other patients already holding the new MRN, shown in a
// "Save anyway?" pop-up. `resolve` answers handleSave's await.
const [mrnDupPrompt, setMrnDupPrompt] = useState(null);

const calculateAge = (dob) => {
  if (!dob) return "";

  const birth = new Date(dob);
  const today = new Date();

  let age = today.getFullYear() - birth.getFullYear();

  const m = today.getMonth() - birth.getMonth();

  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};
  const { id: token } = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("src") || "sheet";
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [coordinatorNoteDraft, setCoordinatorNoteDraft] = useState("");
  const [showUnsavedNotesModal, setShowUnsavedNotesModal] = useState(false);
  const [otherViewerActive, setOtherViewerActive] = useState(false);

  // Custom page scrollbar (dark + light mode) — scoped to this page only.
  useEffect(() => {
    document.documentElement.classList.add("patient-detail-scroll");
    return () => {
      document.documentElement.classList.remove("patient-detail-scroll");
    };
  }, []);

  // "Another user has this open" presence heartbeat — additive only, never
  // blocks or alters the save/load flow.
  useEffect(() => {
    if (loading || !token) return undefined;
    const uid = auth.currentUser?.uid;
    if (!uid) return undefined;

    markPatientPresence(token, uid);
    const heartbeat = setInterval(
      () => markPatientPresence(token, uid),
      PRESENCE_HEARTBEAT_MS
    );
    const unsubscribe = subscribeToOtherViewers(token, uid, setOtherViewerActive);

    return () => {
      clearInterval(heartbeat);
      unsubscribe();
      clearPatientPresence(token, uid);
    };
  }, [loading, token]);
  const [inDuplicates, setInDuplicates] = useState(false);
  // Derived from membership (owner/tenant-admin/site role), never the legacy
  // flat user_roles.role field — see effectiveRole() for why that field is
  // intentionally not consulted here. Reading the flat field left every
  // membership-provisioned user with an empty role, which silently made the
  // patient notes read-only for them.
  const { access, tenantId: activeTenantId, siteId: activeSiteId, membershipLoading } =
    useTenant();
  const resolvedRole = effectiveRole(access, activeTenantId, activeSiteId);
  const currentRole = resolvedRole === "owner" ? "admin" : resolvedRole || "";
  const roleLoading = membershipLoading;
  // Owner is expressed via `globalRole: "owner"` on the user_roles doc (separate
  // from any site role). Kept as its own flag so MRN editing can allow owners.
  const isOwner = Boolean(access?.isOwner);
  // Ensures the "Patient Record Opened" READ audit fires only once per page load.
  const recordOpenedLoggedRef = useRef(false);
  const [physicalAddress, setPhysicalAddress] = useState("");
const [emergencyContactName, setEmergencyContactName] = useState("");
const [emergencyContactPhone, setEmergencyContactPhone] = useState("");


  const [fullName, setFullName] = useState("");
const [mrn, setMrn] = useState("");
  const [dob, setDob] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [allStudies, setAllStudies] = useState([]);
const [potentialStudyIds, setPotentialStudyIds] = useState([]);

// Display-only: studies this patient is already enrolled in, derived by matching
// the patient's name against every study's `patients` subcollection. Purely
// informational — never written back and never affects any save/move workflow.
const [enrolledStudies, setEnrolledStudies] = useState([]);
const [enrolledStudiesLoading, setEnrolledStudiesLoading] = useState(false);

// Potential Studies UI
const [studySearch, setStudySearch] = useState("");
const [showPotentialStudies, setShowPotentialStudies] = useState(false);


  const [callMade, setCallMade] = useState(null);
  const [scheduled, setScheduled] = useState(null);
  const [dateScheduled, setDateScheduled] = useState("");
  // Research-calendar appointment details (single calendar, room-as-metadata).
  // Persisted alongside the patient on both backends; never overwrites the
  // existing callMade/scheduled/dateScheduled fields.
  const [apptStartTime, setApptStartTime] = useState("");
  const [apptEndTime, setApptEndTime] = useState("");
  const [apptRoom, setApptRoom] = useState("");
  const [apptOtherRoom, setApptOtherRoom] = useState("");
  const [apptCoordinator, setApptCoordinator] = useState("");
  const [apptCoordinatorEmail, setApptCoordinatorEmail] = useState("");
  const [apptStudy, setApptStudy] = useState("");
  const [apptVisit, setApptVisit] = useState("");
  const [apptCreatedBy, setApptCreatedBy] = useState("");
  const [calendarEventId, setCalendarEventId] = useState("");
  const [calendarEventLink, setCalendarEventLink] = useState("");
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [rescreenDate, setRescreenDate] = useState("");
  const [status, setStatus] = useState("");
  const [study, setStudy] = useState("");
  const [notes, setNotes] = useState("");
  const [diagnosis, setDiagnosis] = useState([]);
  const [fileUrl, setFileUrl] = useState("");
  // Extra files kept with the patient, read-only here (e.g. a PFT health-survey consent
  // added by a PFT Uploads merge). Never written by this page's Save.
  const [otherFiles, setOtherFiles] = useState([]);
  // Health survey from Files (flag PFT_SURVEY_TEXT_ENABLED, 2026-10-26). An answer, or a
  // "Use survey", can change these fields while the page is open; the page then takes
  // the stored value for each field the person hasn't touched (patientSurvey.js), so
  // Save never writes an older copy back. surveyBaseRef: those fields as last loaded.
  const [surveySubmissionIds, setSurveySubmissionIds] = useState([]);
  // Which notes pop-up is open ("legacy" or null). Display only.
  const [notesViewer, setNotesViewer] = useState(null);
  const surveyBaseRef = useRef(null);
  const surveyRefreshingRef = useRef(false);
  const [uploading, setUploading] = useState(false);
  const normalizedRole = currentRole.toLowerCase();
  // The referral role has full coordinator access (see /coordinator route guards),
  // so treat it exactly like a coordinator for the note controls on this page.
  const isCoordinator =
    normalizedRole === "coordinator" || normalizedRole === "referral";
  const canAddCoordinatorNote =
    normalizedRole === "coordinator" ||
    normalizedRole === "admin" ||
    normalizedRole === "referral";
  const legacyNotesReadOnly = roleLoading || !currentRole || isCoordinator;
  // MRN is read-only for everyone EXCEPT admins/owners, for both stores. RegX
  // patients save it with the record; Sheet patients (Dubois) through the narrow
  // updateSheetPatientMrn, which re-checks admin on the server (2026-10-29).
  const canEditMrn =
    !roleLoading &&
    (normalizedRole === "admin" || normalizedRole === "owner" || isOwner);
  // ============================
// Diagnosis-specific fields
// ============================

// EOS
const [eosNA, setEosNA] = useState(false);
const [eosValues, setEosValues] = useState([{ value: "", date: "" }]);

// Exacerbations
const [exacNA, setExacNA] = useState(false);
const [exacerbations, setExacerbations] = useState([
  { date: "", type: "Outpatient" },
]);

// Age
const [ageNA, setAgeNA] = useState(false);
const [age, setAge] = useState("");

useEffect(() => {
  if (dob && !ageNA) {
    setAge(calculateAge(dob));
  }
}, [dob, ageNA]);

// PFT
const [pftNA, setPftNA] = useState(false);
const [pftDate, setPftDate] = useState("");

const [fev1fvcPre, setFev1fvcPre] = useState("");
const [fev1fvcPost, setFev1fvcPost] = useState("");

const [fev1Pre, setFev1Pre] = useState("");
const [fev1Post, setFev1Post] = useState("");

const [fvcPre, setFvcPre] = useState("");
const [fvcPost, setFvcPost] = useState("");

const [revPercent, setRevPercent] = useState("");
const [revMl, setRevMl] = useState("");

// PFT history (flag PFT_HISTORY_ENABLED): every dated PFT, newest first. Rules
// in services/pftTests.js. The flat fields above stay loaded exactly as before;
// with the flag on, a save writes them from the newest test instead.
const [pftTests, setPftTests] = useState([]);
// What this page loaded: the tests, plus the stored PFT fields and list.
const pftLoadedRef = useRef({ base: [], record: {} });
const [sex, setSex] = useState("");
// Whether the loaded record had a `sex` field: written only once in use.
const sexLoadedRef = useRef(false);
// Smoking status: a patient field, one of SMOKING_OPTIONS (dropdown) plus a free-text
// note. Filled from the newest PFT through the PFT Uploads merge, or set here. Same
// rule as sex: each is written only once in use.
const [smokingStatus, setSmokingStatus] = useState("");
const smokingLoadedRef = useRef(false);
const [smokingNote, setSmokingNote] = useState("");
const smokingNoteLoadedRef = useRef(false);
const rememberPftRecord = useCallback((rec) => {
  const r = rec || {};
  const record = { pftTests: r.pftTests };
  PFT_FLAT_KEYS.forEach((k) => {
    record[k] = r[k];
  });
  const base = testsFromRecord(record);
  pftLoadedRef.current = { base, record };
  setPftTests(base.map((t) => ({ ...t })));
}, []);
// Stable (only refs and setters), so the load effect can list it without re-running.
const loadPftHistory = useCallback((rec) => {
  rememberPftRecord(rec);
  setSex((rec && rec.sex) || "");
  sexLoadedRef.current = !!rec && rec.sex !== undefined && rec.sex !== null;
  setSmokingStatus((rec && rec.smokingStatus) || "");
  smokingLoadedRef.current = !!rec && rec.smokingStatus !== undefined && rec.smokingStatus !== null;
  setSmokingNote((rec && rec.smokingNote) || "");
  smokingNoteLoadedRef.current = !!rec && rec.smokingNote !== undefined && rec.smokingNote !== null;
}, [rememberPftRecord]);
// After a save: the saved copy becomes the new baseline.
const afterPftSave = (saved) => {
  if (!PFT_HISTORY_ENABLED || !saved) return;
  rememberPftRecord({
    ...saved,
    pftTests: saved.pftTests !== undefined ? saved.pftTests : pftLoadedRef.current.record.pftTests,
  });
  if (saved.sex !== undefined) sexLoadedRef.current = true;
  if (saved.smokingStatus !== undefined) smokingLoadedRef.current = true;
  if (saved.smokingNote !== undefined) smokingNoteLoadedRef.current = true;
};

// Methacholine
const [methNA, setMethNA] = useState(false);
const [methResult, setMethResult] = useState("");
const [methDate, setMethDate] = useState("");

// FENO
const [fenoNA, setFenoNA] = useState(false);
const [fenoPPB, setFenoPPB] = useState("");
const [fenoDate, setFenoDate] = useState("");

// Oxygen (COPD)
const [oxygenLitres, setOxygenLitres] = useState("");
const [oxygenType, setOxygenType] = useState("PRN");
const [oxygenNA, setOxygenNA] = useState(false);

// Cancer
const [cancerNA, setCancerNA] = useState(false);
const [cancerStatus, setCancerStatus] = useState("");
const [cancerDate, setCancerDate] = useState("");

// Sleep Apnea
const [bmi, setBmi] = useState("");
const [ahi, setAhi] = useState("");
const [a1c, setA1c] = useState("");
const [a1cDate, setA1cDate] = useState("");
const [diabetes, setDiabetes] = useState("");

// Diagnostics scans. The ref holds the list as loaded (undefined when the record
// has none), so a Sheet save knows whether the patient already uses the field.
const [diagnosticScans, setDiagnosticScans] = useState([]);
const scansLoadedRef = useRef(undefined);
const loadDiagnosticScans = (record) => {
  scansLoadedRef.current = record ? record.diagnosticScans : undefined;
  setDiagnosticScans(scansForEditing(record ? record.diagnosticScans : undefined));
};
const updateScan = (index, patch) =>
  setDiagnosticScans((list) => list.map((s, i) => (i === index ? { ...s, ...patch } : s)));
// ============================
// Study Eligibility
// ============================

const [activeStudies, setActiveStudies] = useState([]);
const [studyEligibility, setStudyEligibility] = useState({});
const [showEligibilityStudies, setShowEligibilityStudies] = useState(false);
const [studyEligibilitySearch, setStudyEligibilitySearch] = useState("");

  const baseStatuses = [
    "Lung Center Referrals",
    "Awaiting PI Review",
    "Pending Medical Records",
    // Stored status strings — "Future Studies - IPF" is shown as "- ILD" via
    // statusLabel (see src/constants/diseaseOptions.js).
    ...DIAGNOSIS_OPTIONS.map((dx) => `Future Studies - ${dx}`),
    "Rescreen",
    "Deceased",
    "Provider to Screen",
    "Provider to Schedule",
    "Ready to Schedule",
    "Patient Scheduled for Screen",
    "Unable to Reach",
    "Not Interested",
    "Screen Failed",
    "Enrolled",
    "Potential Duplicate",
  ];

  // Coordinators are managed in Firestore (the `coordinators` collection) via the
  // admin "Manage Coordinators" page — no longer hardcoded. Load the active ones
  // for the status dropdown; the status-string format ("Pending (Name)") is
  // unchanged, so existing patient assignments keep matching exactly.
  const [coordinators, setCoordinators] = useState([]);

  useEffect(() => {
    let mounted = true;
    fetchActiveCoordinators()
      .then((list) => {
        if (mounted) setCoordinators(list.map((c) => c.name));
      })
      .catch((e) => console.error("Failed to load coordinators:", e));
    return () => {
      mounted = false;
    };
  }, []);

  const diagnosisOptions = DIAGNOSIS_OPTIONS;

  const normalizeDiagnosis = (dx) =>
  Array.isArray(dx) ? dx : dx ? [dx] : [];


const formatDiagnosis = (dx) =>
  Array.isArray(dx) ? dx.join(", ") : dx || "-";

  // Study enrollments as loaded from either backend.
  const loadEnrollments = (record, loadedStatus) => {
    const raw = record ? record.enrolledStudies : undefined;
    const list = normalizeEnrolledStudies(raw);
    enrollLoadedRef.current = {
      present: raw !== undefined && raw !== null && raw !== "",
      list,
    };
    loadedStatusRef.current = loadedStatus;
    setStudyEnrollments(list);
  };

  // Every study at this site, with DISEASE and STATUS, for the Enrolled picker
  // and the move rule. Read from `studies` (not `studies_copy`, which carries
  // only ids and names). Same doc ids as studies_copy, so entries line up with
  // the study-patients master list.
  useEffect(() => {
    let cancelled = false;
    getDocs(collection(db, getStudiesPath()))
      .then((snap) => {
        if (!cancelled) setStudyDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((err) => console.error("Error loading studies for enrollment:", err));
    return () => {
      cancelled = true;
    };
  }, []);
  const studiesById = useMemo(
    () => new Map(studyDocs.map((s) => [s.id, s])),
    [studyDocs]
  );
  const enrollmentView = useMemo(
    () => splitCurrentAndPast(studyEnrollments, studiesById),
    [studyEnrollments, studiesById]
  );
  const activeEnrollmentCount = enrollmentView.current.length + enrollmentView.past.length;
  // DIAGNOSTICS badge, derived from the recorded studies (the stored flag is
  // only used for patients flagged by the old pop-up with no study recorded).
  const diagBadge = useMemo(
    () => diagnosticsBadgeFor({ enrolledStudies: studyEnrollments, enrolledInDiagnostics }, studiesById),
    [studyEnrollments, enrolledInDiagnostics, studiesById]
  );
  const diagStudyNames = diagBadge.studies
    .map((e) => e.studyName || studyDisplayName(studiesById.get(e.studyId)) || e.studyId)
    .join(", ");
  // The most recent return to referrals, for the "moved back" note.
  const returnedToReferralsEntry = useMemo(
    () =>
      activeEnrolledStudies(studyEnrollments)
        .filter((e) => e.returnedAt)
        .sort((a, b) => String(b.returnedAt).localeCompare(String(a.returnedAt)))[0] || null,
    [studyEnrollments]
  );

  // ------------------------
  // Fetch patient data
  // ------------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (source === "sheet" && activeSiteUsesLegacySheet()) {
          const data = await fetchSheetPatients();
          const patient = data.find((entry) => entry.token === token);
          if (patient) {
            setFullName(patient.fullName || "");
            setMrn(patient.mrn || "");
            originalMrnRef.current = patient.mrn || "";
            setDob(patient.dob || "");
            setCallMade(patient.callMade === "TRUE");
            setScheduled(patient.scheduled === "TRUE");
            setDateScheduled(patient.dateScheduled || "");
            // Research-calendar appointment details (stored inside CLINICAL_DATA).
            setApptStartTime(patient.apptStartTime || "");
            setApptEndTime(patient.apptEndTime || "");
            setApptRoom(patient.apptRoom || "");
            setApptOtherRoom(patient.apptOtherRoom || "");
            setApptCoordinator(patient.apptCoordinator || "");
            setApptCoordinatorEmail(patient.apptCoordinatorEmail || "");
            setApptStudy(patient.apptStudy || "");
            setApptVisit(patient.apptVisit || "");
            setApptCreatedBy(patient.apptCreatedBy || "");
            setCalendarEventId(patient.calendarEventId || "");
            setCalendarEventLink(patient.calendarEventLink || "");
            setRescreenDate(patient.rescreenDate || "");
            setStatus(patient.status || "");
            originalStatusRef.current = patient.status || "";
            setStudy(patient.study || "");
            setNotes(patient.notes || "");
            setDiagnosis(normalizeDiagnosis(patient.diagnosis));
            setFileUrl(patient.fileUrl || "");
            // Rides in CLINICAL_DATA for Sheet patients (a PFT health-survey merge).
            setOtherFiles(Array.isArray(patient.otherFiles) ? patient.otherFiles.filter((f) => f && f.url) : []);
            surveyBaseRef.current = surveyFieldsOfRecord(patient);
            setSurveySubmissionIds(surveySubmissionIdsOfRecord(patient));
            // ----------------------
// Restore clinical data (Sheet)
// ----------------------

setEosNA(patient.eosNA ?? false);
setEosValues(patient.eosValues || [{ value: "", date: "" }]);

setExacNA(patient.exacNA ?? false);
setExacerbations(
  patient.exacerbations || [{ date: "", type: "Outpatient" }]
);

setAgeNA(patient.ageNA ?? false);
setAge(patient.age || "");

setPftNA(patient.pftNA ?? false);
setPftDate(patient.pftDate || "");

setFev1fvcPre(patient.fev1fvcPre || "");
setFev1fvcPost(patient.fev1fvcPost || "");

setFev1Pre(patient.fev1Pre || "");
setFev1Post(patient.fev1Post || "");

setFvcPre(patient.fvcPre || "");
setFvcPost(patient.fvcPost || "");

setRevPercent(patient.revPercent || "");
setRevMl(patient.revMl || "");
loadPftHistory(patient);

setMethNA(patient.methNA ?? false);
setMethResult(patient.methResult || "");
setMethDate(patient.methDate || "");

setFenoNA(patient.fenoNA ?? false);
setFenoPPB(patient.fenoPPB || "");
setFenoDate(patient.fenoDate || "");

setOxygenLitres(patient.oxygenLitres || "");
setOxygenType(patient.oxygenType || "PRN");
setOxygenNA(patient.oxygenNA ?? false);

setCancerNA(patient.cancerNA ?? false);
setCancerStatus(patient.cancerStatus || "");
setCancerDate(patient.cancerDate || "");

setBmi(patient.bmi || "");
setAhi(patient.ahi || "");
setA1c(patient.a1c || "");
setA1cDate(patient.a1cDate || "");
setDiabetes(patient.diabetes || "");
loadDiagnosticScans(patient);

setStudyEligibility(patient.studyEligibility || {});
setIsUrgent(patient.urgent || false);
setUrgentReason(patient.urgentReason || "");
setUrgentTimestamp(patient.urgentTimestamp || null);
setOnHold(patient.onHold ?? false);
setSheetMovedToCoordinatorAt(patient.movedToCoordinatorAt || null);
{
  const f = readDiagnosticsFlag(patient);
  diagFlagLoadedRef.current = { present: f.present, value: f.value };
  setEnrolledInDiagnostics(f.value);
  setEnrolledInDiagnosticsAt(f.at);
  setEnrolledInDiagnosticsBy(f.by);
}
loadEnrollments(patient, patient.status || "");
          }
        } else if (source === "firestore" || source === "duplicates") {
          // Look in consents first, then duplicates
          let docRef = doc(db, getConsentsPath(), token);
          let docSnap = await getDoc(docRef);
          let isDup = false;

          if (!docSnap.exists()) {
            const dupRef = doc(db, getDuplicatesPath(), token);
            const dupSnap = await getDoc(dupRef);
            if (dupSnap.exists()) {
              docSnap = dupSnap;
              isDup = true;
              setInDuplicates(true);
            }
          }

          if (docSnap.exists()) {
            const p = docSnap.data();
            setFullName(p.fullName || p.name || "");
            setMrn(p.mrn || "");
            originalMrnRef.current = p.mrn || "";
            setDob(p.dob || "");
            setPhone(p.phone || "");
            setEmail(p.email || "");
            setPotentialStudyIds(p.potentialStudyIds || []);

            setPhysicalAddress(p.physicalAddress || "");
setEmergencyContactName(p.emergencyContactName || "");
setEmergencyContactPhone(p.emergencyContactPhone || "");

            setCallMade(p.callMade ?? null);
            setScheduled(p.scheduled ?? null);
            setDateScheduled(p.dateScheduled || "");
            // Research-calendar appointment details.
            setApptStartTime(p.apptStartTime || "");
            setApptEndTime(p.apptEndTime || "");
            setApptRoom(p.apptRoom || "");
            setApptOtherRoom(p.apptOtherRoom || "");
            setApptCoordinator(p.apptCoordinator || "");
            setApptCoordinatorEmail(p.apptCoordinatorEmail || "");
            setApptStudy(p.apptStudy || "");
            setApptVisit(p.apptVisit || "");
            setApptCreatedBy(p.apptCreatedBy || "");
            setCalendarEventId(p.calendarEventId || "");
            setCalendarEventLink(p.calendarEventLink || "");
            setRescreenDate(p.rescreenDate || "");
            setStatus(p.status || (isDup ? "Potential Duplicate" : "Lung Center Referrals"));
            setStudy(p.study || "");
            setNotes(p.notes || "");
            setDiagnosis(normalizeDiagnosis(p.diagnosis));
            setFileUrl(p.consentUrl || p.fileUrl || "");
            setOtherFiles(Array.isArray(p.otherFiles) ? p.otherFiles.filter((f) => f && f.url) : []);
            surveyBaseRef.current = surveyFieldsOfRecord(p);
            setSurveySubmissionIds(surveySubmissionIdsOfRecord(p));
            // ----------------------
// Restore clinical data
// ----------------------

setEosNA(p.eosNA ?? false);
setEosValues(p.eosValues || [{ value: "", date: "" }]);

setExacNA(p.exacNA ?? false);
setExacerbations(
  p.exacerbations || [{ date: "", type: "Outpatient" }]
);

setAgeNA(p.ageNA ?? false);
setAge(p.age || "");

setPftNA(p.pftNA ?? false);
setPftDate(p.pftDate || "");

setFev1fvcPre(p.fev1fvcPre || "");
setFev1fvcPost(p.fev1fvcPost || "");

setFev1Pre(p.fev1Pre || "");
setFev1Post(p.fev1Post || "");

setFvcPre(p.fvcPre || "");
setFvcPost(p.fvcPost || "");

setRevPercent(p.revPercent || "");
setRevMl(p.revMl || "");
loadPftHistory(p);

setMethNA(p.methNA ?? false);
setMethResult(p.methResult || "");
setMethDate(p.methDate || "");

setFenoNA(p.fenoNA ?? false);
setFenoPPB(p.fenoPPB || "");
setFenoDate(p.fenoDate || "");

setOxygenLitres(p.oxygenLitres || "");
setOxygenType(p.oxygenType || "PRN");
setOxygenNA(p.oxygenNA ?? false);

setCancerNA(p.cancerNA ?? false);
setCancerStatus(p.cancerStatus || "");
setCancerDate(p.cancerDate || "");

setBmi(p.bmi || "");
setAhi(p.ahi || "");
setA1c(p.a1c || "");
setA1cDate(p.a1cDate || "");
setDiabetes(p.diabetes || "");
loadDiagnosticScans(p);

setStudyEligibility(p.studyEligibility || {});
setIsUrgent(p.urgent || false);
setUrgentReason(p.urgentReason || "");
setUrgentTimestamp(p.urgentTimestamp || null);
setOnHold(p.onHold ?? false);
{
  const f = readDiagnosticsFlag(p);
  diagFlagLoadedRef.current = { present: f.present, value: f.value };
  setEnrolledInDiagnostics(f.value);
  setEnrolledInDiagnosticsAt(f.at);
  setEnrolledInDiagnosticsBy(f.by);
}
loadEnrollments(p, p.status || (isDup ? "Potential Duplicate" : "Lung Center Referrals"));
          }
        }
      } catch (err) {
        console.error("❌ Error fetching patient:", err);
        setToast({ type: "error", msg: "Failed to load patient data." });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, source, loadPftHistory]);

  // ------------------------
  // Centralized audit: log a single READ event per page load
  // ------------------------
  useEffect(() => {
    if (loading) return;
    if (!token) return;
    if (recordOpenedLoggedRef.current) return;
    recordOpenedLoggedRef.current = true;
    logPatientAuditEvent({
      patientId: token,
      title: "Patient Record Opened",
      action: "PATIENT_RECORD_VIEWED",
      type: "READ",
      severity: "info",
      patientName: fullName,
      mrnLast4: getMrnLast4(mrn),
      source,
      actorEmail: auth.currentUser?.email || "unknown",
      actorUid: auth.currentUser?.uid || null,
      metadata: {
        page: "PatientDetailPage",
        inDuplicates,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, token]);

  // ------------------------
// Fetch ACTIVE studies for diagnosis
// ------------------------
useEffect(() => {
  const fetchActiveStudies = async () => {

    if (!diagnosis.length) {
  setActiveStudies([]);
  return;
}
    try {

      const snap = await getDocs(collection(db, getStudiesPath()));

      const filtered = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter((s) =>
  s.STATUS === "ACTIVE" &&
  diagnosis.some(
    (dx) =>
      (s.DISEASE || "").toLowerCase().trim() ===
      dx.toLowerCase().trim()
  )
);

      setActiveStudies(filtered);

    } catch (err) {
      console.error("Error loading active studies", err);
    }

  };

  fetchActiveStudies();

}, [diagnosis]);

  useEffect(() => {
  const fetchStudies = async () => {
    try {
      const snap = await getDocs(collection(db, getStudiesCopyPath()));
      // `merged` marks an old list joined into its current study
      // (studyCopyMerge.js). Potential Studies still shows one if this patient
      // has it ticked, labelled, so it can always be seen and unticked; the
      // enrolled-match hint below skips it.
      const formatted = snap.docs.map((d) => {
        const s = d.data();
        const merged = isMergedStudyCopy(s);
        const base =
          s.NAME && s.STUDY_ID
            ? `${s.NAME}: ${s.STUDY_ID}`
            : s.NAME || s.STUDY_ID || d.id;
        return {
          id: d.id,
          merged,
          label: merged ? `${base} (old list)` : base,
        };
      });
      setAllStudies(formatted);
    } catch (err) {
      console.error("❌ Error fetching studies_copy:", err);
    }
  };

  fetchStudies();
}, []);

// Display-only: derive which studies this patient is already enrolled in by
// matching their name across every study's `patients` subcollection. This is a
// read-only lookup that runs when the patient/name or study list changes; it
// never writes anything and does not touch the save/move/potential-study flows.
useEffect(() => {
  const normalizeName = (v) =>
    (v || "").toString().trim().toLowerCase().replace(/\s+/g, " ");

  const target = normalizeName(fullName);

  // No usable name → nothing to look up. Note we intentionally do NOT bail when
  // `allStudies` is empty: the legacy Sheet lookup below must still run for
  // sheet-based patients even when there are no Firestore studies to scan.
  if (!target) {
    setEnrolledStudies([]);
    return;
  }

  let cancelled = false;

  const deriveEnrolledStudies = async () => {
    setEnrolledStudiesLoading(true);
    try {
      // 1) Firestore store: match name across every study's `patients` subcollection.
      const firestoreMatches = (
        await Promise.all(
          allStudies.filter((study) => !study.merged).map(async (study) => {
            try {
              const snap = await getDocs(
                collection(db, getStudyPatientsPath(study.id))
              );
              const isEnrolled = snap.docs.some((d) => {
                const data = d.data();
                return normalizeName(data.NAME || data.name) === target;
              });
              return isEnrolled ? study : null;
            } catch {
              // Ignore per-study read failures; this is a best-effort display.
              return null;
            }
          })
        )
      ).filter(Boolean);

      // "Enrolled" is derived ONLY from real enrollment records (the study's
      // `patients` subcollection above). The legacy Google Sheet is deliberately
      // NOT used here: its `study` field is a NOTE, not an enrollment fact — a
      // sheet row can name a study while its message is DNQ/screen-fail, so the
      // patient was never enrolled. Notes are shown in "Notes & Study"; they do
      // not determine enrolled-vs-not.
      const combined = [];
      const seenLabels = new Set();
      for (const s of firestoreMatches) {
        const key = (s.label || "").trim().toLowerCase();
        if (!key || seenLabels.has(key)) continue;
        seenLabels.add(key);
        combined.push(s);
      }

      if (!cancelled) setEnrolledStudies(combined);
    } catch (err) {
      console.error("Error deriving enrolled studies:", err);
      if (!cancelled) setEnrolledStudies([]);
    } finally {
      if (!cancelled) setEnrolledStudiesLoading(false);
    }
  };

  deriveEnrolledStudies();

  return () => {
    cancelled = true;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [fullName, allStudies]);

// Helper: build a base payload of identifiers shared by every audit event
// written from this page. Centralized so the call sites stay short.
const buildAuditBase = () => ({
  patientId: token,
  patientName: fullName,
  mrnLast4: getMrnLast4(mrn),
  source,
  actorEmail: auth.currentUser?.email || "unknown",
  actorUid: auth.currentUser?.uid || null,
  metadata: {
    page: "PatientDetailPage",
    recordPath:
      source === "sheet"
        ? `sheet/${token}`
        : `${inDuplicates ? "duplicates" : "consents"}/${token}`,
  },
});

// PFT soft checks (services/pftSoftChecks.js): a "Verified" ticked or unticked in this
// save is audited, with the PFT date and field name only, never the value. Called after a
// successful save, before afterPftSave resets what the page loaded. Fail-soft like every
// audit event.
const auditSoftCheckVerifications = async () => {
  if (!PFT_HISTORY_ENABLED) return;
  for (const c of verificationChanges(pftLoadedRef.current.base, pftTests)) {
    await logPatientAuditEvent({
      ...buildAuditBase(),
      title: c.verified ? "PFT value verified against the PDF" : "PFT value no longer marked verified",
      action: c.verified ? "PFT_VALUE_VERIFIED" : "PFT_VALUE_UNVERIFIED",
      type: "WRITE",
      severity: "info",
      changes: [
        {
          field: `PFT ${c.date || "(no date)"}: ${c.label}`,
          from: c.verified ? "not verified" : "verified",
          to: c.verified ? "verified" : "not verified",
        },
      ],
      metadata: { ...buildAuditBase().metadata, pftTestId: c.testId, field: c.key },
    });
  }
};

// The "Enrolled in Diagnostics" fields for a save. Omitted entirely for records
// that never carried the flag and don't have it now, so ordinary saves add no
// new fields; once present they're always written (cleared = false/null).
const diagnosticsFlagFields = (alreadyPresent) => {
  if (!enrolledInDiagnostics && !alreadyPresent) return {};
  return {
    enrolledInDiagnostics: !!enrolledInDiagnostics,
    enrolledInDiagnosticsAt: enrolledInDiagnostics ? enrolledInDiagnosticsAt || null : null,
    enrolledInDiagnosticsBy: enrolledInDiagnostics ? enrolledInDiagnosticsBy || null : null,
  };
};

// The `enrolledStudies` field for a save, merged over the copy stored right now
// (`freshRaw`) so a change made elsewhere since this page loaded is kept.
// Omitted for records that never used it and still don't. Sheet patients: it
// rides inside CLINICAL_DATA, which updatePatientInSheet REPLACES, so once a
// row has it, it must be sent on every save.
const enrollmentFieldsFor = (freshRaw) => {
  const freshList = normalizeEnrolledStudies(freshRaw);
  const present =
    enrollLoadedRef.current.present || freshList.length > 0 || studyEnrollments.length > 0;
  if (!present) return { fields: {}, list: [], freshList };
  const list = mergeEntryUpdates(freshList, studyEnrollments);
  return { fields: { enrolledStudies: list }, list, freshList };
};

// The status to save. Normally the page's status. The one exception: while
// this page was open, someone else returned the patient to referrals (an admin
// closing a study, the backfill) and this user didn't touch the status. Saving
// the stale "Enrolled" would silently undo that move, so the stored
// Lung Center Referrals is kept instead.
const statusToSave = (freshStatus, freshList) => {
  if (status !== loadedStatusRef.current) return status; // the user changed it
  if ((freshStatus || "") !== REFERRALS_STATUS) return status;
  const loaded = new Map(enrollLoadedRef.current.list.map((e) => [entryKey(e), e]));
  const returnedElsewhere = normalizeEnrolledStudies(freshList).some(
    (e) => e.returnedAt && !(loaded.get(entryKey(e)) || {}).returnedAt
  );
  return returnedElsewhere ? REFERRALS_STATUS : status;
};

// Audit study enrollments added, dismissed, verified, and a return to
// referrals, against the list this page loaded. Fail-soft, like every audit.
const emitEnrollmentAudits = async (prevList, nextList, prevStatus, nextStatus, base) => {
  const { added, removed } = diffEnrolledStudies(prevList, nextList);
  const prevByKey = new Map(normalizeEnrolledStudies(prevList).map((e) => [entryKey(e), e]));
  const label = (e) => e.studyName || studyDisplayName(studiesById.get(e.studyId)) || e.studyId;

  for (const e of added) {
    await logPatientAuditEvent({
      ...base,
      title: "Enrolled in Study",
      action: "ENROLLED_STUDY_ADDED",
      changes: [{ field: "enrolledStudies", from: null, to: label(e) }],
      metadata: {
        ...base.metadata,
        studyId: e.studyId,
        disease: e.disease || null,
        approval: e.approval || null,
      },
    });
  }
  for (const e of removed) {
    await logPatientAuditEvent({
      ...base,
      title: "Study Enrollment Dismissed",
      action: "ENROLLED_STUDY_DISMISSED",
      severity: "warning",
      changes: [{ field: "enrolledStudies", from: label(e), to: null }],
      metadata: { ...base.metadata, studyId: e.studyId, kept: "entry kept on record with dismissedAt" },
    });
  }
  for (const e of normalizeEnrolledStudies(nextList)) {
    const before = prevByKey.get(entryKey(e));
    if (e.verifiedAt && !(before && before.verifiedAt)) {
      await logPatientAuditEvent({
        ...base,
        title: "Study Enrollment Verified",
        action: "ENROLLED_STUDY_VERIFIED",
        changes: [{ field: "enrolledStudies", from: `${label(e)} (${e.matchScore ?? "?"}% match)`, to: `${label(e)} (verified)` }],
        metadata: { ...base.metadata, studyId: e.studyId, matchScore: e.matchScore ?? null },
      });
    }
  }

  const newlyReturned = normalizeEnrolledStudies(nextList).filter((e) => {
    const before = prevByKey.get(entryKey(e));
    return e.returnedAt && !e.dismissedAt && !(before && before.returnedAt);
  });
  if (newlyReturned.length && (nextStatus || "") === REFERRALS_STATUS) {
    const reason = newlyReturned[0].returnedReason || null;
    await logPatientAuditEvent({
      ...base,
      title: "Returned to Lung Center Referrals",
      action: "RETURNED_TO_REFERRALS",
      changes: [{ field: "status", from: prevStatus || null, to: REFERRALS_STATUS }],
      metadata: {
        ...base.metadata,
        reason,
        reasonText: RETURN_REASON_TEXT[reason] || null,
        studies: newlyReturned.map((e) => ({ studyId: e.studyId, studyName: label(e) })),
      },
    });
  }
};

// Enrolled pop-up confirmed: add the picked studies, then let the shared rule
// decide where the patient goes. Nothing is written until Save.
const handleEnrollConfirm = (picks, outcome) => {
  const now = new Date().toISOString();
  const by = auth.currentUser?.email || "unknown";
  const additions = coordinatorPickEntries(picks, { at: now, by });
  let next = mergeEnrolledStudies(studyEnrollments, additions);

  if (outcome.move) {
    next = stampReturn(next, studiesById, { at: now, by, reason: outcome.reason });
    setStatus(REFERRALS_STATUS);
    // Keep the existing DIAGNOSTICS badge and Enrolled-box note working.
    if (outcome.reason !== "closed" && !enrolledInDiagnostics) {
      setEnrolledInDiagnostics(true);
      setEnrolledInDiagnosticsAt(now);
      setEnrolledInDiagnosticsBy(by);
    }
    setToast({
      type: "success",
      msg: `${RETURN_REASON_TEXT[outcome.reason]}, so this patient will move back to ${REFERRALS_STATUS} for other studies. Click Save to keep this.`,
    });
  } else {
    setStatus("Enrolled");
    setToast({ type: "success", msg: "Study added. Click Save to keep this." });
  }
  setStudyEnrollments(next);
  setRescreenDate("");
  setShowEnrollPicker(false);
};

// Dismiss one enrollment. A pick that was never saved is simply dropped; a
// saved one is kept on the record with dismissedAt/By, never deleted.
const dismissEnrollment = (entry) => {
  const k = entryKey(entry);
  const wasSaved = enrollLoadedRef.current.list.some((e) => entryKey(e) === k);
  setStudyEnrollments((list) =>
    wasSaved
      ? list.map((e) =>
          entryKey(e) === k
            ? { ...e, dismissedAt: new Date().toISOString(), dismissedBy: auth.currentUser?.email || "unknown" }
            : e
        )
      : list.filter((e) => entryKey(e) !== k)
  );
};

// Staff confirm a backfill match is right. The score is hidden from then on.
// A low score was the only thing holding the patient in Enrolled, so the same
// rule then runs again: if every study is diagnostics or closed, and no other
// match is still waiting for review under 90%, the patient moves back to
// referrals, exactly as when a coordinator picks the study. Saved on Save.
const verifyEnrollment = (entry) => {
  const k = entryKey(entry);
  const now = new Date().toISOString();
  const by = auth.currentUser?.email || "unknown";
  const next = studyEnrollments.map((e) =>
    entryKey(e) === k ? { ...e, verifiedAt: now, verifiedBy: by } : e
  );
  if (!applyReturnIfDue(next, "Verified. ")) {
    setStudyEnrollments(next);
    setToast({ type: "success", msg: "Verified. Click Save to keep this." });
  }
};

// Should this Enrolled patient go back to referrals right now? True when every
// study is diagnostics or closed and no match under 90% is still unverified.
const returnDueFor = (list) => {
  const outcome = shouldReturnToReferrals(list, studiesById);
  const stillUnsure = unverifiedLowMatches(list).length > 0;
  return { due: isEnrolledStatus(status) && outcome.move && !stillUnsure, outcome };
};

// Move back to referrals when due (state only; Save writes it and audits
// RETURNED_TO_REFERRALS). Returns false, changing nothing, when not due.
const applyReturnIfDue = (list, toastPrefix = "") => {
  const { due, outcome } = returnDueFor(list);
  if (!due) return false;
  const now = new Date().toISOString();
  const by = auth.currentUser?.email || "unknown";
  setStudyEnrollments(stampReturn(list, studiesById, { at: now, by, reason: outcome.reason }));
  setStatus(REFERRALS_STATUS);
  setRescreenDate("");
  if (outcome.reason !== "closed" && !enrolledInDiagnostics) {
    setEnrolledInDiagnostics(true);
    setEnrolledInDiagnosticsAt(now);
    setEnrolledInDiagnosticsBy(by);
  }
  setToast({
    type: "success",
    msg: `${toastPrefix}${RETURN_REASON_TEXT[outcome.reason]}, so this patient will move back to ${REFERRALS_STATUS} for other studies. Click Save to keep this.`,
  });
  return true;
};

// Audit the diagnostics flag being set or cleared (both backends).
const emitDiagnosticsFlagAudit = async (prevData, newData, base) => {
  const prevFlag = !!(prevData && prevData.enrolledInDiagnostics);
  const nextFlag = !!(newData && newData.enrolledInDiagnostics);
  if (prevFlag === nextFlag) return;
  await logPatientAuditEvent({
    ...base,
    title: nextFlag ? "Enrolled in Diagnostics Flagged" : "Enrolled in Diagnostics Cleared",
    action: nextFlag ? "DIAGNOSTICS_ENROLLMENT_FLAGGED" : "DIAGNOSTICS_ENROLLMENT_CLEARED",
    severity: "info",
    changes: [
      { field: "enrolledInDiagnostics", from: prevFlag, to: nextFlag },
      ...(nextFlag && newData?.enrolledInDiagnosticsAt
        ? [{ field: "enrolledInDiagnosticsAt", from: null, to: newData.enrolledInDiagnosticsAt }]
        : []),
    ],
  });
};

// Compose grouped audit events from a prev→next snapshot pair.
// Writes one event per logical group (status, urgent, potential studies,
// profile fields, clinical fields). Each call is fail-soft via the service.
const emitGroupedSaveAudits = async (prevData, newData, baseOverride) => {
  const base = baseOverride || buildAuditBase();

  // Status change as its own event
  if ((prevData?.status || "") !== (newData?.status || "")) {
    await logPatientAuditEvent({
      ...base,
      title: "Status Changed",
      action: "PATIENT_STATUS_UPDATED",
      changes: [
        {
          field: "status",
          from: prevData?.status || null,
          to: newData?.status || null,
        },
      ],
    });
  }

  // Urgent flag transitions
  const prevUrgent = !!(prevData && prevData.urgent);
  const nextUrgent = !!(newData && newData.urgent);
  if (prevUrgent !== nextUrgent) {
    await logPatientAuditEvent({
      ...base,
      title: nextUrgent ? "Urgent Flag Enabled" : "Urgent Flag Disabled",
      action: nextUrgent ? "URGENT_FLAG_ENABLED" : "URGENT_FLAG_DISABLED",
      severity: nextUrgent ? "warning" : "info",
      changes: [
        { field: "urgent", from: prevUrgent, to: nextUrgent },
        ...(nextUrgent && newData?.urgentReason
          ? [
              {
                field: "urgentReason",
                from: prevData?.urgentReason || null,
                to: newData?.urgentReason || null,
              },
            ]
          : []),
      ],
    });
  } else if (
    nextUrgent &&
    (prevData?.urgentReason || "") !== (newData?.urgentReason || "")
  ) {
    await logPatientAuditEvent({
      ...base,
      title: "Urgent Reason Updated",
      action: "URGENT_REASON_UPDATED",
      severity: "warning",
      changes: [
        {
          field: "urgentReason",
          from: prevData?.urgentReason || null,
          to: newData?.urgentReason || null,
        },
      ],
    });
  }

  // "Enrolled in Diagnostics" flag set / cleared
  await emitDiagnosticsFlagAudit(prevData, newData, base);

  // Potential study assignment changes
  const potentialChanges = buildPatientDiff(
    { potentialStudyIds: prevData?.potentialStudyIds || [] },
    { potentialStudyIds: newData?.potentialStudyIds || [] },
    ["potentialStudyIds"]
  );
  if (potentialChanges.length) {
    const prevIds = prevData?.potentialStudyIds || [];
    const nextIds = newData?.potentialStudyIds || [];
    const added = nextIds.filter((id) => !prevIds.includes(id));
    const removed = prevIds.filter((id) => !nextIds.includes(id));
    await logPatientAuditEvent({
      ...base,
      title: "Potential Study Assignment Updated",
      action: "POTENTIAL_STUDY_ASSIGNMENT_UPDATED",
      changes: potentialChanges,
      metadata: {
        ...base.metadata,
        added,
        removed,
      },
    });
  }

  // Generic profile fields (excluding status — already logged above)
  const profileFields = PATIENT_PROFILE_FIELDS;
  const profileChanges = buildPatientDiff(prevData || {}, newData || {}, profileFields);
  if (profileChanges.length) {
    await logPatientAuditEvent({
      ...base,
      title: "Patient Fields Updated",
      action: "PATIENT_FIELDS_UPDATED",
      changes: profileChanges,
    });
  }

  // Clinical fields
  const clinicalChanges = buildPatientDiff(
    prevData || {},
    newData || {},
    PATIENT_CLINICAL_FIELDS
  );
  if (clinicalChanges.length) {
    await logPatientAuditEvent({
      ...base,
      title: "Clinical Fields Updated",
      action: "CLINICAL_FIELDS_UPDATED",
      changes: clinicalChanges,
    });
  }
};
  // ------------------------
  // Save changes (with auto move)
  // ------------------------
  // ------------------------
// Save changes (with full MRN-preserving move logic)
// ------------------------
const handleSave = async () => {
  if (coordinatorNoteDraft.trim().length > 0) {
    setShowUnsavedNotesModal(true);
    return;
  }
  // A health-survey answer is being pulled into the page right now: saving this
  // instant could write the older copy back.
  if (surveyRefreshingRef.current) {
    setToast({ type: "error", msg: "This patient's survey answers just came in. The page is updating; save again in a moment." });
    return;
  }
  try {
    setSaving(true);
    if (status === "Rescreen" && !rescreenDate) {
  setToast({ type: "error", msg: "Rescreen date is required." });
  setSaving(false);
  return;
}
    // A patient newly marked Enrolled must be tied to at least one study.
    // Patients who were already Enrolled before this feature save as before.
    if (
      isEnrolledStatus(status) &&
      !isEnrolledStatus(loadedStatusRef.current) &&
      activeEnrolledStudies(studyEnrollments).length === 0
    ) {
      setToast({ type: "error", msg: "Pick the study this patient is enrolled in." });
      setShowEnrollPicker(true);
      setSaving(false);
      return;
    }
    // Admin MRN edit (both stores): warn, never block, when another patient at
    // this site already has the new MRN. If the check itself can't load, the
    // save goes ahead as it would have before this check existed.
    const mrnEdited = canEditMrn && mrnChanged(originalMrnRef.current, mrn);
    if (mrnEdited) {
      let others = [];
      try {
        others = otherPatientsWithMrn(mrn, await loadDuplicateCandidates(), { id: token, source });
      } catch (e) {
        console.warn("MRN duplicate check unavailable:", e);
      }
      if (others.length) {
        const goAhead = await new Promise((resolve) => setMrnDupPrompt({ matches: others, resolve }));
        setMrnDupPrompt(null);
        if (!goAhead) {
          setSaving(false);
          return;
        }
      }
    }
    // MRN_CHANGED audit (last 4 digits only), after the write that saved it.
    const auditMrnChange = async (from, recordPath) => {
      if (!mrnEdited || !mrnChanged(from, mrn)) return;
      await logPatientAuditEvent({
        ...buildAuditBase(),
        metadata: { ...buildAuditBase().metadata, recordPath },
        title: "MRN changed",
        action: "MRN_CHANGED",
        type: "WRITE",
        severity: "warning",
        changes: [mrnChangeAudit(from, mrn)],
      });
    };
    // After a successful save: audit enrollment changes and reset the baseline.
    // Diffed against the copy that was STORED just before this save
    // (`freshList`), not the one this page loaded, so a change someone else
    // made meanwhile (an admin rejecting, a study closing) is never logged as
    // this user's.
    const afterEnrollmentSave = async (savedList, base, freshList, savedStatus) => {
      await emitEnrollmentAudits(
        freshList,
        savedList,
        loadedStatusRef.current,
        savedStatus,
        base
      );
      // New coordinator picks go to an admin for approval onto the master
      // list; a pick removed before approval withdraws its request.
      const { added, removed } = diffEnrolledStudies(freshList, savedList);
      const toRequest = added.filter((e) => e.source === "coordinator" && e.approval === "pending");
      if (toRequest.length) {
        const failed = await createEnrollmentRequests({
          patientId: token,
          source,
          patientName: fullName,
          dob,
          mrn,
          entries: toRequest,
        });
        // An alert, not a toast: Save navigates away right after, so a toast
        // would never be seen.
        if (failed) {
          window.alert(
            "The patient was saved, but the approval request couldn't be sent to an admin. Please let an admin know."
          );
        }
      }
      const toCancel = removed.filter((e) => e.approval === "pending");
      if (toCancel.length) await cancelEnrollmentRequests(token, toCancel);
      enrollLoadedRef.current = {
        present: enrollLoadedRef.current.present || savedList.length > 0,
        list: savedList,
      };
      loadedStatusRef.current = savedStatus;
      if (savedStatus !== status) setStatus(savedStatus);
      setStudyEnrollments(savedList);
    };
    if (source === "sheet") {
      // Re-read the row's current enrollments first, so this save can't erase
      // one written since the page loaded (CLINICAL_DATA is replaced whole).
      // Only when enrollments are in use, so ordinary saves are unchanged. A
      // failed read fails the save rather than risk overwriting.
      let freshSheetEnrollments;
      let freshSheetStatus;
      if (enrollLoadedRef.current.present || studyEnrollments.length > 0) {
        const rows = await fetchSheetPatients();
        const freshRow = rows.find((r) => r.token === token);
        freshSheetEnrollments = freshRow?.enrolledStudies;
        freshSheetStatus = freshRow?.status;
      }
      const sheetEnroll = enrollmentFieldsFor(freshSheetEnrollments);
      const sheetSaveStatus = statusToSave(freshSheetStatus, sheetEnroll.freshList);
      // Mirrors the Firestore branch's movedToCoordinatorAt logic below: reset
      // the clock whenever status just changed into (or between) a coordinator
      // box, so the stale-box reminder workflow ages from the actual move.
      const sheetStatusChanged = originalStatusRef.current !== status;
      let nextMovedToCoordinatorAt = sheetMovedToCoordinatorAt || null;
      if (sheetStatusChanged && status.includes("(")) {
        nextMovedToCoordinatorAt = new Date().toISOString();
      }
      // PFT history: the newest test goes to the flat fields; the list rides in
      // CLINICAL_DATA once in use. The row is re-read first when a list is in
      // play, so a PFT merged in since this page loaded isn't erased. A failed
      // read fails the save rather than risk overwriting.
      let sheetPftFields = {};
      if (PFT_HISTORY_ENABLED) {
        let storedPft = pftLoadedRef.current.record;
        if (Array.isArray(storedPft.pftTests) || needsTestList(pftTests)) {
          const rows = await fetchSheetPatients();
          const freshRow = rows.find((r) => r.token === token);
          if (!freshRow) throw new Error("Couldn't re-read this patient before saving their PFTs.");
          storedPft = freshRow;
        }
        sheetPftFields = {
          ...pftFieldsForSave({ stored: storedPft, base: pftLoadedRef.current.base, local: pftTests }),
          ...(sex || sexLoadedRef.current ? { sex } : {}),
          ...(smokingStatus || smokingLoadedRef.current ? { smokingStatus } : {}),
          ...(smokingNote || smokingNoteLoadedRef.current ? { smokingNote } : {}),
        };
      }
      // Admin MRN edit: its own one-column write, BEFORE the full save, so a
      // refusal (changed meanwhile, not an admin) saves nothing at all. The full
      // save below never carries an MRN.
      if (mrnEdited) {
        try {
          await updateSheetPatientMrn({ token, from: originalMrnRef.current, to: mrn });
        } catch (err) {
          if (err instanceof SheetWriteRefusedError) {
            setToast({ type: "error", msg: `Nothing was saved. ${err.message}` });
            setSaving(false);
            return;
          }
          throw err;
        }
        const mrnFrom = originalMrnRef.current;
        originalMrnRef.current = mrn.trim();
        await auditMrnChange(mrnFrom, `sheet/${token}`);
      }
      // Diagnostics scans: only once the patient has one. The server merges the
      // list with the row's by scan id (clinicalForPatientSave).
      const sheetScanFields = scanFieldsForSave(scansLoadedRef.current, diagnosticScans);
      // Update Google Sheet record
      await updateSheetPatient({
  token,
  status: sheetSaveStatus || "Pending",
  callMade: !!callMade,
  scheduled: !!scheduled,
  dateScheduled,
  // Research-calendar appointment details — not top-level sheet columns, so these
  // ride inside CLINICAL_DATA (spread back out on read). No sheet structure change.
  apptStartTime,
  apptEndTime,
  apptRoom,
  apptOtherRoom,
  apptCoordinator,
  apptCoordinatorEmail,
  apptStudy,
  apptVisit,
  apptCreatedBy,
  calendarEventId,
  calendarEventLink,
  rescreenDate,
  fullName,
  dob,
  phone,
  email,

  study,
  notes,
  diagnosis,
  fileUrl,

  physicalAddress,
  emergencyContactName,
  emergencyContactPhone,

  potentialStudyIds,

  eosNA,
  eosValues,

  exacNA,
  exacerbations,

  ageNA,
  age,

  lastEditedBy: auth.currentUser?.email || "unknown",

  pftNA,
  pftDate,
  fev1fvcPre,
  fev1fvcPost,
  fev1Pre,
  fev1Post,
  fvcPre,
  fvcPost,
  revPercent,
  revMl,

  methNA,
  methResult,
  methDate,

  fenoNA,
  fenoPPB,
  fenoDate,

  oxygenLitres,
  oxygenType,
  oxygenNA,

  cancerNA,
  cancerStatus,
  cancerDate,

  bmi,
  ahi,
  a1c,
  a1cDate,
  diabetes,
  urgent: isUrgent,
urgentReason,
urgentTimestamp,
onHold,
movedToCoordinatorAt: nextMovedToCoordinatorAt,
// Rides inside CLINICAL_DATA, which updatePatientInSheet REPLACES with this
// payload — so once a row has the flag it must be re-sent on every save.
...diagnosticsFlagFields(diagFlagLoadedRef.current.present),
// Same rule for the study enrollments.
...sheetEnroll.fields,
...sheetScanFields,

  studyEligibility,
  // Last, so the newest PFT test overrides the flat fields above (flag on only).
  ...sheetPftFields
});
      // What was just saved is the baseline for further saves in this session.
      if (sheetScanFields.diagnosticScans) scansLoadedRef.current = sheetScanFields.diagnosticScans;
      // ---- AUDIT: Google Sheet update succeeded ----
      await logPatientAuditEvent({
        ...buildAuditBase(),
        title: "Google Sheet Record Updated",
        action: "GOOGLE_SHEET_RECORD_UPDATED",
        type: "WRITE",
        severity: "info",
        changes: [
          {
            field: "status",
            from: null,
            to: sheetSaveStatus || "Pending",
          },
        ],
        metadata: {
          ...buildAuditBase().metadata,
          urgent: !!isUrgent,
          rescreenDate: rescreenDate || null,
        },
      });
      // The Sheet event above doesn't diff fields, so log the diagnostics flag
      // transition explicitly against the value this page loaded.
      await emitDiagnosticsFlagAudit(
        { enrolledInDiagnostics: diagFlagLoadedRef.current.value },
        { enrolledInDiagnostics, enrolledInDiagnosticsAt },
        buildAuditBase()
      );
      diagFlagLoadedRef.current = {
        present: diagFlagLoadedRef.current.present || enrolledInDiagnostics,
        value: enrolledInDiagnostics,
      };
      await auditSoftCheckVerifications();
      afterPftSave(sheetPftFields);
      await afterEnrollmentSave(sheetEnroll.list, buildAuditBase(), sheetEnroll.freshList, sheetSaveStatus);
      // Keep the reset-detection baseline current for further saves in this session.
      originalStatusRef.current = sheetSaveStatus;
      setSheetMovedToCoordinatorAt(nextMovedToCoordinatorAt);
    } else if (source === "firestore" || source === "duplicates") {
      const consRef = doc(db, getConsentsPath(), token);
      const dupRef = doc(db, getDuplicatesPath(), token);
      const consSnap = await getDoc(consRef);
      const dupSnap = await getDoc(dupRef);
      const prevData = consSnap.exists()
  ? consSnap.data()
  : dupSnap.exists()
  ? dupSnap.data()
  : {};

const prevPotentialStudyIds = prevData?.potentialStudyIds || [];
const wasUrgent = prevData?.urgent || false;
const statusChanged = prevData?.status !== status;
let movedToCoordinatorAt = prevData?.movedToCoordinatorAt || null;

if (statusChanged && status.includes("(")) {
  movedToCoordinatorAt = new Date().toISOString();
}
      // Merged over the stored copy (prevData was just read), so enrollments
      // written elsewhere since this page loaded survive this save.
      const fsEnroll = enrollmentFieldsFor(prevData?.enrolledStudies);
      const fsSaveStatus = statusToSave(prevData?.status, fsEnroll.freshList);
      // Base patient data (editable fields)
      const data = {
        lastEditedBy: auth.currentUser?.email || "unknown",
lastEditedAt: serverTimestamp(),
        // MRN is written back only when the current user is allowed to edit it
        // (admin/owner). For everyone else it's omitted, so the stored MRN is
        // preserved untouched exactly as before.
        ...(canEditMrn ? { mrn } : {}),
        eosNA,
  eosValues,
  rescreenDate,
  exacNA,
  exacerbations,
  ageNA,
  age,
  pftNA,
  pftDate,
  fev1fvcPre,
  fev1fvcPost,
  fev1Pre,
  fev1Post,
  fvcPre,
  fvcPost,
  revPercent,
  revMl,
  methNA,
  methResult,
  methDate,
  fenoNA,
  fenoPPB,
  fenoDate,
  oxygenLitres,
  oxygenType,
  studyEligibility,
  cancerNA,
  cancerStatus,
  cancerDate,
  bmi,
  ahi,
  a1c,
  a1cDate,
  diabetes,
        name: fullName,
        dob,
        phone,
        email,
        potentialStudyIds,
        physicalAddress,
emergencyContactName,
emergencyContactPhone,

        callMade,
        scheduled,
        dateScheduled,
        // Research-calendar appointment details (additive; existing fields untouched).
        apptStartTime,
        apptEndTime,
        apptRoom,
        apptOtherRoom,
        apptCoordinator,
        apptCoordinatorEmail,
        apptStudy,
        apptVisit,
        apptCreatedBy,
        calendarEventId,
        calendarEventLink,
        status: fsSaveStatus || "Lung Center Referrals",
        study,
        notes,
        diagnosis,
        consentUrl: fileUrl,
        urgent: isUrgent,
urgentReason,
urgentTimestamp,
movedToCoordinatorAt,
onHold,
        ...diagnosticsFlagFields(
          diagFlagLoadedRef.current.present ||
            (prevData && prevData.enrolledInDiagnostics !== undefined)
        ),
        ...fsEnroll.fields,
        // Diagnostics scans: only once the patient has one, merged over the copy
        // just read, so a scan added elsewhere since this page loaded is kept.
        ...scanFieldsForSave(prevData?.diagnosticScans, diagnosticScans),
        // PFT history (flag on only): merged over the copy just read, then the
        // newest test overrides the flat fields above. Sex only once in use.
        ...(PFT_HISTORY_ENABLED
          ? {
              ...pftFieldsForSave({
                stored: prevData || {},
                base: pftLoadedRef.current.base,
                local: pftTests,
              }),
              ...(sex || (prevData && prevData.sex !== undefined) ? { sex } : {}),
              ...(smokingStatus || (prevData && prevData.smokingStatus !== undefined) ? { smokingStatus } : {}),
              ...(smokingNote || (prevData && prevData.smokingNote !== undefined) ? { smokingNote } : {}),
            }
          : {}),
      };
      // Health survey (flag on, only for a patient with survey activity): anything an
      // answer or a "Use survey" saved since this page loaded is kept, and the
      // person's own edits win (patientSurvey.js mergeSurveyChanges). Otherwise as before.
      if (surveyBaseRef.current && patientSurvey.status) {
        const m = mergeSurveyChanges(
          { phone, physicalAddress, email, bmi, notes, diagnosis, fileUrl },
          surveyBaseRef.current,
          surveyFieldsOfRecord(prevData || {})
        );
        Object.assign(data, {
          phone: m.phone,
          physicalAddress: m.physicalAddress,
          email: m.email,
          bmi: m.bmi,
          notes: m.notes,
          diagnosis: m.diagnosis,
          consentUrl: m.fileUrl,
        });
      }
      // CASE 0: Patient currently in "consents" and now marked as Potential Duplicate
if (consSnap.exists() && status === "Potential Duplicate") {
  const consData = consSnap.data();

  // Move everything from consents → duplicates, preserving all data
  await setDoc(dupRef, {
    ...consData,
    ...data,
    movedToDuplicatesAt: serverTimestamp(),
  });

  // Delete from consents to prevent duplication
  await deleteDoc(consRef);
  setInDuplicates(true);

  setToast({
    type: "warn",
    msg: "⚠️ Patient moved to Duplicates successfully.",
  });

  setSaving(false);
  // ---- AUDIT: moved to duplicates ----
  await logPatientAuditEvent({
    ...buildAuditBase(),
    metadata: {
      page: "PatientDetailPage",
      recordPath: `duplicates/${token}`,
      movedFrom: "consents",
    },
    title: "Moved to Potential Duplicate",
    action: "MOVED_TO_POTENTIAL_DUPLICATE",
    type: "WRITE",
    severity: "warning",
    changes: [
      {
        field: "status",
        from: prevData?.status || null,
        to: "Potential Duplicate",
      },
    ],
  });
  await emitDiagnosticsFlagAudit(prevData, data, {
    ...buildAuditBase(),
    metadata: { page: "PatientDetailPage", recordPath: `duplicates/${token}` },
  });
  diagFlagLoadedRef.current = {
    present: diagFlagLoadedRef.current.present || enrolledInDiagnostics,
    value: enrolledInDiagnostics,
  };
  await afterEnrollmentSave(
    fsEnroll.list,
    {
      ...buildAuditBase(),
      metadata: { page: "PatientDetailPage", recordPath: `duplicates/${token}` },
    },
    fsEnroll.freshList,
    fsSaveStatus
  );
  await auditMrnChange(prevData?.mrn, `duplicates/${token}`);
  return; // 🔁 Exit early to avoid running other update branches
}


      // CASE 1: Patient currently in "duplicates" but changed to another status
      if (dupSnap.exists() && status !== "Potential Duplicate") {
        const dupData = dupSnap.data();

        // Merge all previous fields (preserves MRN, initials, token, etc.)
        await setDoc(consRef, {
          ...dupData,
          ...data,
          movedBackAt: serverTimestamp(),
        });

        // Remove from duplicates
        await deleteDoc(dupRef);
        setInDuplicates(false);
        setToast({
          type: "success",
          msg: "✅ Patient moved back to Consents and saved successfully.",
        });
        // ---- AUDIT: restored from duplicates ----
        const restoredBase = {
          ...buildAuditBase(),
          metadata: {
            page: "PatientDetailPage",
            recordPath: `consents/${token}`,
            movedFrom: "duplicates",
          },
        };
        await logPatientAuditEvent({
          ...restoredBase,
          title: "Restored from Duplicates",
          action: "RESTORED_FROM_DUPLICATES",
          type: "WRITE",
          severity: "warning",
          changes: [
            {
              field: "status",
              from: "Potential Duplicate",
              to: status,
            },
          ],
        });
        // Also emit grouped audits for any non-status changes in this save.
        // Status was already captured in "Restored from Duplicates" above —
        // align the snapshot so the grouped emitter doesn't double-log it.
        await emitGroupedSaveAudits(
          { ...(prevData || {}), status: data.status },
          data,
          restoredBase
        );
      }

      else if (dupSnap.exists()) {
  await updateDoc(dupRef, data);

  // ---- AUDIT: grouped diff against prevData (duplicates branch) ----
  await emitGroupedSaveAudits(prevData || {}, data);
}

      // CASE 3: Already in consents, normal update
else if (consSnap.exists()) {
  await updateDoc(consRef, data);

  // ---- AUDIT: grouped diff against prevData (consents branch) ----
  await emitGroupedSaveAudits(prevData || {}, data);
}

      // CASE 4: Not found anywhere (failsafe)
      else {
        throw new Error("Patient record not found in Firestore.");
      }
      await auditMrnChange(
        prevData?.mrn,
        `${dupSnap.exists() && status === "Potential Duplicate" ? "duplicates" : "consents"}/${token}`
      );
      if (mrnEdited) originalMrnRef.current = mrn;
      diagFlagLoadedRef.current = {
        present: diagFlagLoadedRef.current.present || enrolledInDiagnostics,
        value: enrolledInDiagnostics,
      };
      await auditSoftCheckVerifications();
      afterPftSave(data);
      await afterEnrollmentSave(fsEnroll.list, {
        ...buildAuditBase(),
        metadata: {
          page: "PatientDetailPage",
          // Case 2 updated the duplicates doc in place; every other case
          // leaves the record in consents.
          recordPath: `${
            dupSnap.exists() && status === "Potential Duplicate" ? "duplicates" : "consents"
          }/${token}`,
        },
      }, fsEnroll.freshList, fsSaveStatus);
      // --- Sync Potential Patients mirror docs under studies_copy ---
const removed = prevPotentialStudyIds.filter(
  (id) => !potentialStudyIds.includes(id)
);
const added = potentialStudyIds.filter(
  (id) => !prevPotentialStudyIds.includes(id)
);

// delete removed mirror docs
await Promise.all(
  removed.map(async (studyId) => {
    await deleteDoc(
      doc(db, getStudyPotentialPatientsPath(studyId), token)
    );
  })
);

// create/update added mirror docs
await Promise.all(
  potentialStudyIds.map(async (studyId) => {
    await setDoc(
      doc(db, getStudyPotentialPatientsPath(studyId), token),
      {
        token,
        name: fullName,
        dob,
        phone,
        email,
        addedAt: new Date(),
      },
      { merge: true }
    );
  })
);
    }

    // ------------------------
// 🚨 URGENT EMAIL TRIGGER
// ------------------------


if (isUrgent) {
    const getCoordinatorFromStatus = (status) => {
    const match = status.match(/\((.*?)\)/);
    return match ? match[1] : null;
  };

  const coordinator = getCoordinatorFromStatus(status);

  // Whether this coordinator receives urgent emails is now decided server-side
  // by sendUrgentEmail (per the coordinator's `receivesUrgentEmail` flag in the
  // `coordinators` collection) — no hardcoded names here.
  if (coordinator) {
    try {
      await authedFetch(
        `${FUNCTIONS_BASE}/sendUrgentEmail`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            coordinator,
            status,
            urgentReason,
            urgentTimestamp,
            // Look up the coordinator in THIS site's collection, not the
            // deployment's default site (so an Ohio alert finds Ohio's
            // coordinator). Server falls back to its default if these are absent.
            tenantId: activeTenantId,
            siteId: activeSiteId,
          }),
        }
      );
    } catch (err) {
      console.error("❌ Urgent email failed:", err);
    }
  }
}
    // ✅ Delay to ensure Firestore commits before navigation
    await new Promise((resolve) => setTimeout(resolve, 400));

    setToast({ type: "success", msg: "Saved successfully." });
    goBackToOrigin();

  } catch (err) {
    console.error("❌ Failed to update patient:", err);
    setToast({ type: "error", msg: "Save failed. Please try again." });
  } finally {
    setSaving(false);
  }
};


  // ------------------
  // ------
  // Upload file
  // ------------------------
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setToast(null);

    try {
      // The Drive upload targets the ONE legacy Google Drive folder that belongs
      // to the legacy-sheet site. `source` defaults to "sheet" when the URL has
      // no ?src=, so without this guard a patient opened at any OTHER site would
      // POST their consent file into that site's Drive — a cross-site PHI write.
      // Mirrors the read guard in fetchData above; see `activeSiteUsesLegacySheet`.
      if (source === "sheet" && activeSiteUsesLegacySheet()) {
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const base64 = reader.result.split(",")[1];
            const response = await fetch(
              DRIVE_UPLOAD_URL,
              {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({
                  file: base64,
                  filename: file.name,
                  mimeType: file.type,
                }),
              }
            );
            const result = await response.json();
            if (result.success) {
              const url = `https://drive.google.com/file/d/${result.fileId}/view`;
              await updateSheetPatient({ token, fileUrl: url });
              setFileUrl(url);
              setToast({ type: "success", msg: "File uploaded." });
              // ---- AUDIT: consent file uploaded (sheet branch) ----
              await logPatientAuditEvent({
                ...buildAuditBase(),
                title: "Consent File Uploaded",
                action: "CONSENT_FILE_UPLOADED",
                type: "WRITE",
                severity: "info",
                metadata: {
                  ...buildAuditBase().metadata,
                  fileName: file.name,
                  mimeType: file.type,
                  uploadTarget: "google_drive",
                },
              });
            } else {
              throw new Error("Upload failed.");
            }
          } catch (err) {
            console.error("❌ Upload error:", err);
            setToast({ type: "error", msg: "Upload failed." });
          } finally {
            setUploading(false);
          }
        };
        reader.readAsDataURL(file);
      } else if (source === "firestore" || source === "duplicates") {
        const storagePath = `manual_uploads/${token}_${file.name}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        await updateDoc(doc(db, inDuplicates ? getDuplicatesPath() : getConsentsPath(), token), {
          consentUrl: url,
        });
        setFileUrl(url);
        setToast({ type: "success", msg: "File uploaded." });
        // ---- AUDIT: consent file uploaded (firestore branch) ----
        await logPatientAuditEvent({
          ...buildAuditBase(),
          title: "Consent File Uploaded",
          action: "CONSENT_FILE_UPLOADED",
          type: "WRITE",
          severity: "info",
          metadata: {
            ...buildAuditBase().metadata,
            fileName: file.name,
            mimeType: file.type,
            storagePath,
            uploadTarget: "firebase_storage",
          },
        });
        setUploading(false);
      } else {
        // No branch handled it — e.g. source === "sheet" at a site that does not
        // use the legacy sheet. Say so and clear the spinner; falling through
        // silently would leave "Uploading…" on screen forever.
        setToast({
          type: "error",
          msg: "File upload isn't available for this patient at this site.",
        });
        setUploading(false);
      }
    } catch (err) {
      console.error("❌ Upload error:", err);
      setToast({ type: "error", msg: "Upload failed." });
      setUploading(false);
    }
  };

  // ------------------------
  // UI helpers
  // ------------------------
  // Where leaving this page lands. Used by BOTH the Back button and the
  // post-save navigation, which used to carry copies of this decision and
  // could drift apart.
  //
  // The study branch navigates to an explicit path rather than navigate(-1):
  // after a save there are extra entries in the history, and a refresh on this
  // page would otherwise send Back somewhere unrelated.
  const goBackToOrigin = () => {
    const from = location.state?.from;
    if (from === "study" && location.state?.studyId) {
      navigate(`/study/${location.state.studyId}`);
      return;
    }
    if (from === "query") {
      navigate(-1);
      return;
    }
    // UI revamp: opened from the personal home page, so go back there.
    if (UI_REVAMP_ENABLED && from === "home") {
      navigate("/home");
      return;
    }
    navigate("/coordinator", {
      state: {
        fromCoordinatorTab: location.state?.fromCoordinatorTab || "statuses",
        fromCoordinatorSection: location.state?.fromCoordinatorSection || null,
        fromCoordinatorPatientId:
          location.state?.fromCoordinatorPatientId || null,
      },
    });
  };

  // ---- Unsaved changes (2026-10-07) ----
  // Everything Save writes, plus an unsaved coordinator note. Leaving by Back / Cancel
  // with any of it changed asks first; so does closing or reloading the tab. Save &
  // Return is unchanged: it leaves directly once the save succeeds.
  const unsavedSnapshot = {
    status, fullName, dob, mrn, phone, email, physicalAddress, emergencyContactName,
    emergencyContactPhone, callMade, scheduled, dateScheduled, rescreenDate, study, notes,
    diagnosis, potentialStudyIds, studyEligibility, eosNA, eosValues, exacNA, exacerbations,
    ageNA, age, pftNA, pftDate, fev1fvcPre, fev1fvcPost, fev1Pre, fev1Post, fvcPre, fvcPost,
    revPercent, revMl, methNA, methResult, methDate, fenoNA, fenoPPB, fenoDate, oxygenLitres,
    oxygenType, oxygenNA, cancerNA, cancerStatus, cancerDate, bmi, ahi, a1c, a1cDate, diabetes,
    diagnosticScans, isUrgent, urgentReason, onHold, enrolledInDiagnostics, studyEnrollments, pftTests, sex,
    smokingStatus, smokingNote, apptStartTime, apptEndTime, apptRoom, apptOtherRoom,
    apptCoordinator, apptCoordinatorEmail, apptStudy, apptVisit, apptCreatedBy,
    calendarEventId, calendarEventLink, coordinatorNoteDraft: coordinatorNoteDraft.trim(),
  };
  const { dirty: hasUnsavedChanges, onInteract: onPageInteract, absorb: absorbSaved } = useUnsavedChanges(
    unsavedSnapshot,
    !loading && !enrolledStudiesLoading
  );

  // ---- Health survey from Files (flag PFT_SURVEY_TEXT_ENABLED, 2026-10-26) ----
  // RegX patients and (Dubois) Sheet patients; never Duplicates.
  const surveyPatient = useMemo(() => {
    if (!PFT_SURVEY_TEXT_ENABLED || loading) return null;
    if (source === "sheet" && !activeSiteUsesLegacySheet()) return null;
    const store = patientSurveyStore({ source, inDuplicates });
    return store ? { store, id: token } : null;
  }, [loading, source, inDuplicates, token]);
  const patientSurvey = usePatientSurvey(surveyPatient);
  const surveyView = surveyPatient && patientSurvey.loaded
    ? surveyPanelView({ status: patientSurvey.status, card: patientSurvey.card, hasFile: !!fileUrl, submissionIds: surveySubmissionIds })
    : null;
  // When an answer or a "Use survey" lands (lastAppliedAt moves), re-read the record and
  // take the stored value of each field the person hasn't touched.
  const surveySeenRef = useRef(undefined);
  useEffect(() => {
    if (!surveyPatient || !patientSurvey.loaded) return;
    const at = (patientSurvey.status && patientSurvey.status.lastAppliedAt) || "";
    const first = surveySeenRef.current === undefined;
    if (!first && at === surveySeenRef.current) return;
    surveySeenRef.current = at;
    if (!at) return;
    // (First look: refresh once, in case an answer landed between loading and now.)
    const base = surveyBaseRef.current;
    if (!base) return;
    surveyRefreshingRef.current = true;
    (async () => {
      try {
        let rec = null;
        if (surveyPatient.store === "sheet") {
          const rows = await fetchSheetPatients();
          rec = rows.find((r) => r.token === token) || null;
        } else {
          const snap = await getDoc(doc(db, getConsentsPath(), token));
          rec = snap.exists() ? snap.data() : null;
        }
        if (!rec) return;
        const stored = surveyFieldsOfRecord(rec);
        setPhone((v) => mergeSurveyField("phone", v, base.phone, stored.phone));
        setPhysicalAddress((v) => mergeSurveyField("physicalAddress", v, base.physicalAddress, stored.physicalAddress));
        setEmail((v) => mergeSurveyField("email", v, base.email, stored.email));
        setBmi((v) => mergeSurveyField("bmi", v, base.bmi, stored.bmi));
        setNotes((v) => mergeSurveyField("notes", v, base.notes, stored.notes));
        setDiagnosis((v) => mergeSurveyField("diagnosis", v, base.diagnosis, stored.diagnosis));
        setFileUrl((v) => mergeSurveyField("fileUrl", v, base.fileUrl, stored.fileUrl));
        setOtherFiles(Array.isArray(rec.otherFiles) ? rec.otherFiles.filter((f) => f && f.url) : []);
        setSurveySubmissionIds(surveySubmissionIdsOfRecord(rec));
        surveyBaseRef.current = stored;
        absorbSaved({
          phone: stored.phone,
          physicalAddress: stored.physicalAddress,
          email: stored.email,
          bmi: stored.bmi,
          notes: stored.notes,
          diagnosis: stored.diagnosis,
        });
      } catch (e) {
        console.warn("Couldn't refresh after the health survey:", e && e.message);
      } finally {
        surveyRefreshingRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyPatient, patientSurvey.loaded, patientSurvey.status]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const handleBack = () => {
    if (hasUnsavedChanges) setShowLeaveModal(true);
    else goBackToOrigin();
  };

  const sectionOpenSettersRef = useRef({});
  const registerSectionOpenSetter = useCallback((sectionId, setOpenFn) => {
    sectionOpenSettersRef.current[sectionId] = setOpenFn;
    return () => {
      delete sectionOpenSettersRef.current[sectionId];
    };
  }, []);

  const scrollToSection = (id) => {
    sectionOpenSettersRef.current[id]?.(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  };

  // Section navigator entries (conditional ones only appear when relevant)
  const showClinicalAsthma = diagnosis.includes("Asthma");
  const showClinicalCOPD = diagnosis.includes("COPD");
  const showClinicalBronch = diagnosis.includes("Bronchiectasis");
  const showClinicalSleep = diagnosis.includes("Sleep Apnea");
  const showClinicalDiagnostics = diagnosis.includes("Diagnostics");
  const showAnyClinical = showClinicalAsthma || showClinicalCOPD || showClinicalBronch || showClinicalSleep;

  const sectionNav = [
    { id: "sec-identity", label: "Identity", Icon: Users },
    { id: "sec-contact", label: "Contact & Emergency", Icon: MapPin },
    { id: "sec-potential", label: "Potential Studies", Icon: Stethoscope },
    { id: "sec-scheduling", label: "Call / Scheduling", Icon: Calendar },
    { id: "sec-status", label: "Status & Diagnosis", Icon: Activity },
    ...(PFT_HISTORY_ENABLED ? [{ id: "sec-pft", label: "PFT", Icon: HeartPulse }] : []),
    ...(showClinicalAsthma
      ? [{ id: "sec-asthma", label: "Asthma Data", Icon: Wind }]
      : []),
    ...(showClinicalCOPD
      ? [{ id: "sec-copd", label: "COPD Data", Icon: Cigarette }]
      : []),
    ...(showClinicalBronch
      ? [{ id: "sec-bronch", label: "Bronchiectasis Data", Icon: AirVent }]
      : []),
    ...(showClinicalSleep
      ? [{ id: "sec-sleep", label: "Sleep Apnea Data", Icon: Moon }]
      : []),
    ...(showClinicalDiagnostics
      ? [{ id: "sec-diagnostics", label: "Diagnostics Data", Icon: ScanLine }]
      : []),
    ...(activeStudies.length > 0
      ? [{ id: "sec-eligibility", label: "Study Eligibility", Icon: CheckCircle2 }]
      : []),
    { id: "sec-notes", label: "Notes & Study", Icon: FileText },
    { id: "sec-coordinator-notes", label: "Coordinator Notes", Icon: ClipboardList },
    { id: "sec-files", label: "Files", Icon: Upload },
  ];

  if (loading) {
    return (
      <div className="relative min-h-screen w-full text-slate-100 font-[Manrope] flex items-center justify-center">
        <PageBackground />
        <div className="relative flex flex-col items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl blur-md opacity-70" />
            <div className="relative w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            </div>
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-slate-400 font-medium">
            Loading patient…
          </p>
        </div>
      </div>
    );
  }

  return (
    <SectionExpandContext.Provider value={registerSectionOpenSetter}>
    <div
      className="relative min-h-screen w-full text-slate-100 font-[Manrope]"
      onPointerDownCapture={onPageInteract}
      onKeyDownCapture={onPageInteract}
    >
      <PageBackground />

      {/* Sticky top action bar */}
      <header className="sticky top-0 z-40 backdrop-blur-2xl bg-slate-950/55 border-b border-white/10 shadow-[0_8px_30px_-12px_rgba(124,58,237,0.35)]">
        <div className="max-w-[1700px] mx-auto px-3 sm:px-4 py-2 flex items-center gap-2 sm:gap-3">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 text-xs text-slate-300 hover:text-white transition shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Back</span>
          </button>

          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-sm font-bold text-white ring-1 ring-white/20 shrink-0">
              {(fullName || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold leading-none">
                Patient Follow-Up
              </div>
              <div className="flex items-center gap-1.5 min-w-0">
                <h1 className="text-sm sm:text-base font-semibold tracking-tight text-white leading-tight truncate">
                  {fullName || "Unnamed Patient"}
                </h1>
                {mrn && (
                  <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/[0.05] border border-white/10 text-[10px] font-mono text-purple-200 shrink-0">
                    <IdCard className="w-2.5 h-2.5 text-purple-300" />
                    {mrn}
                  </span>
                )}
                {!ageNA && age !== "" && age !== null && age !== undefined && (
                  <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/[0.05] border border-white/10 text-[10px] font-medium text-blue-200 shrink-0">
                    Age {age}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {isUrgent && (
              <span className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-500/20 border border-rose-400/40 text-[10px] font-bold text-rose-100 shadow-[0_0_12px_-2px_rgba(244,63,94,0.6)]">
                <Flame className="w-3 h-3" />
                URGENT
              </span>
            )}
            <Chip tone={inDuplicates ? "danger" : statusTone(status)}>
              {inDuplicates ? "Potential Duplicate" : statusLabel(status) || "Unspecified"}
            </Chip>
            {diagBadge.show && (
              <span
                className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/20 border border-emerald-400/40 text-[10px] font-bold text-emerald-100"
                title={diagBadge.legacy ? "No study recorded" : diagStudyNames}
              >
                <Stethoscope className="w-3 h-3" />
                ENROLLED IN DIAGNOSTICS
              </span>
            )}
            <div className="relative group/savewarn">
              {/* Warning tooltip — appears on hover below the header button */}
              <div className="absolute top-full right-0 mt-2 w-60 pointer-events-none opacity-0 group-hover/savewarn:opacity-100 transition-opacity duration-200 z-50">
                <div className="save-return-warning-arrow absolute -top-1 right-5 w-2 h-2 bg-rose-600 border-l border-t border-rose-400/60 rotate-45" />
                <div className="save-return-warning flex items-start gap-1.5 px-2.5 py-2 rounded-lg bg-rose-600 border border-rose-400/60 text-[10px] text-white font-medium leading-snug shadow-[0_4px_20px_-4px_rgba(244,63,94,0.6)]">
                  <AlertTriangle className="save-return-warning-icon w-3 h-3 mt-0.5 shrink-0 text-rose-200" />
                  <span>This button does not save coordinator notes. Remember to save your notes separately before leaving.</span>
                </div>
              </div>
              <button
                onClick={handleSave}
                disabled={saving}
                className="group relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 relative animate-spin" />
                ) : (
                  <Save className="w-3.5 h-3.5 relative" />
                )}
                <span className="relative hidden sm:inline">
                  {saving ? "Saving…" : "Save & Return"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Toast */}
      <AnimatePresence>
      {toast && (
        <motion.div
          key="patient-toast"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className="max-w-[1700px] mx-auto px-3 sm:px-4 pt-2"
        >
          <div
            className={`rounded-xl border px-3 py-2 text-xs flex items-center gap-2 backdrop-blur-md ${
              toast.type === "success"
                ? "bg-emerald-500/10 border-emerald-400/30 text-emerald-200"
                : toast.type === "error"
                ? "bg-rose-500/10 border-rose-400/30 text-rose-200"
                : "bg-amber-500/15 border-amber-400/30 text-amber-200"
            }`}
          >
            {toast.type === "success" ? (
              <CheckCircle2 className="w-3.5 h-3.5" />
            ) : toast.type === "error" ? (
              <AlertTriangle className="w-3.5 h-3.5" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            {toast.msg}
            <button
              onClick={() => setToast(null)}
              className="ml-auto text-current opacity-70 hover:opacity-100"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Concurrent viewer notice — subtle, never reveals who else is viewing */}
      {otherViewerActive && (
        <div className="max-w-[1700px] mx-auto px-3 sm:px-4 pt-2">
          <div className="rounded-xl border px-3 py-2 text-xs flex items-center gap-2 backdrop-blur-md bg-amber-500/15 border-amber-400/30 text-amber-200">
            <Users className="w-3.5 h-3.5 shrink-0" />
            Another user currently has this patient record open.
          </div>
        </div>
      )}

      {/* Body */}
      <div className="max-w-[1700px] mx-auto px-3 sm:px-4 py-3 grid grid-cols-1 lg:grid-cols-12 gap-3 pb-24">
        {/* Section nav */}
        <aside className="hidden lg:block lg:col-span-2">
          <div className="sticky top-[64px] rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-2 max-h-[calc(100vh-80px)] overflow-y-auto scrollbar-thin">
            <div className="px-2 pb-1.5 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-purple-300" />
              <span className="text-[9px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
                Sections
              </span>
            </div>
            <ul className="space-y-0.5">
              {sectionNav.map((s) => {
                const Icon = s.Icon;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(s.id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-slate-300 hover:text-white hover:bg-white/[0.05] border border-transparent hover:border-white/10 transition group"
                    >
                      <span className="w-6 h-6 rounded-md bg-white/[0.04] border border-white/10 flex items-center justify-center group-hover:border-purple-300/30 transition shrink-0">
                        <Icon className="w-3 h-3 text-purple-300" />
                      </span>
                      <span className="truncate">{s.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        {/* Form column */}
        <div className="lg:col-span-7 space-y-3 min-w-0">
          {/* Patient Identity */}
          <Section
            id="sec-identity"
            title="Patient Identity"
            subtitle="Primary identifiers"
            icon={<Users className="w-3.5 h-3.5 text-purple-300" />}
            defaultOpen
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block sm:col-span-2">
                <span className={labelCls}>Full Name</span>
                <input
                  type="text"
                  className={inputCls}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="e.g., Jane Doe"
                />
              </label>
              <label className="block">
                <span className={labelCls}>MRN</span>
                <div className="relative">
                  <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                  <input
                    type="text"
                    value={mrn}
                    readOnly={!canEditMrn}
                    onChange={
                      canEditMrn ? (e) => setMrn(e.target.value) : undefined
                    }
                    className={
                      canEditMrn
                        ? `${inputCls} pl-9 font-mono`
                        : `${inputCls} pl-9 bg-white/[0.02] text-slate-400 cursor-not-allowed font-mono`
                    }
                  />
                </div>
              </label>
              <label className="block">
                <span className={labelCls}>Date of Birth</span>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                  <input
                    type="date"
                    className={`${inputDateCls} pl-9`}
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                  />
                </div>
              </label>
              <label className="block">
                <span className={labelCls}>Phone</span>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                  <input
                    type="tel"
                    className={`${inputCls} pl-9`}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g., 512-555-0199"
                  />
                </div>
              </label>
              {PFT_HISTORY_ENABLED && (
                <label className="block">
                  <span className={labelCls}>Sex</span>
                  <select className={selectCls} value={sex} onChange={(e) => setSex(e.target.value)}>
                    <option value="">Not recorded</option>
                    {["Female", "Male", "Other"].map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                    {sex && !["Female", "Male", "Other"].includes(sex) && <option value={sex}>{sex}</option>}
                  </select>
                </label>
              )}
              {PFT_HISTORY_ENABLED && (
                <div className="block sm:col-span-2">
                  <span className={labelCls}>Smoking status</span>
                  <div className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-2">
                    <select
                      className={selectCls}
                      value={smokingStatus}
                      onChange={(e) => setSmokingStatus(e.target.value)}
                      aria-label="Smoking status"
                    >
                      <option value="">Not recorded</option>
                      {SMOKING_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                      {/* Never drop a stored value that isn't one of the choices. */}
                      {smokingStatus && !SMOKING_OPTIONS.includes(smokingStatus) && (
                        <option value={smokingStatus}>{smokingStatus}</option>
                      )}
                    </select>
                    <input
                      type="text"
                      className={inputCls}
                      value={smokingNote}
                      onChange={(e) => setSmokingNote(e.target.value)}
                      placeholder="Note (optional), e.g. quit 2010, 30 pack-years"
                      aria-label="Smoking status note"
                    />
                  </div>
                </div>
              )}
              <label className="block">
                <span className={labelCls}>Email</span>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                  <input
                    type="email"
                    className={`${inputCls} pl-9`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="e.g., patient@email.com"
                  />
                </div>
              </label>
            </div>
          </Section>

          {/* Contact & Emergency */}
          <Section
            id="sec-contact"
            title="Contact & Emergency"
            subtitle={
              // An address from a PFT not yet checked against the PDF (soft check): said here
              // too, since this section starts closed.
              PFT_HISTORY_ENABLED && addressChecksOf(pftTests, physicalAddress).some((x) => !x.check.verified)
                ? "Address and emergency contact · address to verify against a PFT"
                : "Address and emergency contact"
            }
            icon={<MapPin className="w-3.5 h-3.5 text-purple-300" />}
            defaultOpen={false}
          >
            <div className="space-y-3">
              <label className="block">
                <span className={labelCls}>Physical Address</span>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                  <input
                    type="text"
                    className={`${inputCls} pl-9`}
                    value={physicalAddress}
                    onChange={(e) => setPhysicalAddress(e.target.value)}
                    placeholder="e.g., 123 Main St, Austin, TX 78701"
                  />
                </div>
              </label>
              {PFT_HISTORY_ENABLED && (
                <AddressSoftCheck
                  tests={pftTests}
                  address={physicalAddress}
                  userEmail={auth.currentUser?.email || ""}
                  onChange={setPftTests}
                  onError={(msg) => setToast({ type: "error", msg })}
                />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className={labelCls}>Emergency Contact Name</span>
                  <div className="relative">
                    <UserCircle2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <input
                      type="text"
                      className={`${inputCls} pl-9`}
                      value={emergencyContactName}
                      onChange={(e) => setEmergencyContactName(e.target.value)}
                      placeholder="e.g., Jane Doe"
                    />
                  </div>
                </label>
                <label className="block">
                  <span className={labelCls}>Emergency Contact Phone</span>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                    <input
                      type="tel"
                      className={`${inputCls} pl-9`}
                      value={emergencyContactPhone}
                      onChange={(e) => setEmergencyContactPhone(e.target.value)}
                      placeholder="e.g., 512-555-0199"
                    />
                  </div>
                </label>
              </div>
            </div>
          </Section>

          {/* Enrolled Studies — the studies this patient is enrolled in, picked
              when they're marked Enrolled (or matched by the one-time backfill,
              with a score until staff verify it). Closed studies move to
              "Previously enrolled" and are never removed. When nothing is
              recorded yet, the old name-only lookup is shown as a hint. */}
          {(activeEnrollmentCount > 0 ||
            enrollmentView.past.length > 0 ||
            isEnrolledStatus(status) ||
            enrolledStudiesLoading ||
            enrolledStudies.length > 0) && (
            <Section
              id="sec-enrolled"
              title="Enrolled Studies"
              subtitle="Studies this patient is enrolled in"
              icon={<ClipboardList className="w-3.5 h-3.5 text-emerald-300" />}
              defaultOpen={true}
              headerExtra={
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-300/30 text-[10px] font-semibold text-emerald-200">
                  {enrollmentView.current.length} current
                  {enrollmentView.past.length ? ` · ${enrollmentView.past.length} past` : ""}
                </span>
              }
            >
              {enrollmentView.current.length > 0 && (
                <div className="space-y-2">
                  {enrollmentView.current.map((e) => {
                    const s = studiesById.get(e.studyId);
                    const diag = s ? isDiagnosticsStudy(s) : /diagnostics/i.test(e.disease || "");
                    const scored = hasUnverifiedScore(e);
                    const high = scored && e.matchScore >= 90;
                    const unsaved = !enrollLoadedRef.current.list.some((x) => entryKey(x) === entryKey(e));
                    return (
                      <div
                        key={entryKey(e)}
                        className={`rounded-xl border px-3 py-2.5 ${
                          scored && !high
                            ? "border-amber-400/30 bg-amber-400/10"
                            : "border-emerald-400/30 bg-emerald-500/15 shadow-[0_0_20px_-10px_rgba(52,211,153,0.6)]"
                        }`}
                      >
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Stethoscope className="w-3.5 h-3.5 text-emerald-300 shrink-0" />
                          <span className="text-sm font-semibold text-white">
                            {e.studyName || studyDisplayName(s) || e.studyId}
                          </span>
                          {diag && <Chip tone="info">DIAGNOSTICS</Chip>}
                          {scored && (
                            <Chip tone={high ? "success" : "warn"}>
                              {e.matchScore}% match{high ? "" : " · needs review"}
                            </Chip>
                          )}
                          {e.verifiedAt && <Chip tone="success">VERIFIED</Chip>}
                          {e.approval === "pending" && <Chip tone="warn">WAITING FOR ADMIN APPROVAL</Chip>}
                          {e.approval === "approved" && e.source === "coordinator" && (
                            <Chip tone="success">APPROVED</Chip>
                          )}
                          {unsaved && <Chip>NOT SAVED YET</Chip>}
                        </div>
                        <p className="mt-1 text-[11px] text-slate-400">
                          {e.source === "backfill"
                            ? `Matched from the study's enrolled list by name + date of birth${
                                e.matchReasons?.length ? ` (${e.matchReasons.join("; ")})` : ""
                              }.`
                            : `Added${e.enrolledAt ? ` ${new Date(e.enrolledAt).toLocaleDateString()}` : ""}${
                                e.enrolledBy ? ` by ${e.enrolledBy}` : ""
                              }.`}
                          {e.verifiedAt
                            ? ` Verified ${new Date(e.verifiedAt).toLocaleDateString()}${e.verifiedBy ? ` by ${e.verifiedBy}` : ""}.`
                            : ""}
                        </p>
                        {(scored || (e.approval === "pending" && e.source === "coordinator")) && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {scored && (
                              <button
                                type="button"
                                onClick={() => verifyEnrollment(e)}
                                className="inline-flex items-center gap-1 rounded-lg border border-emerald-300/40 bg-emerald-500/20 px-2.5 py-1 text-[11px] font-semibold text-emerald-100 hover:bg-emerald-500/30 transition"
                              >
                                <CheckCircle2 className="w-3 h-3" />
                                Verify — this is right
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => dismissEnrollment(e)}
                              className="inline-flex items-center gap-1 rounded-lg border border-white/15 bg-white/[0.05] px-2.5 py-1 text-[11px] font-semibold text-slate-300 hover:bg-white/[0.1] transition"
                            >
                              <X className="w-3 h-3" />
                              {scored ? "Not this patient" : "Remove"}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Still Enrolled although nothing holds them there any more
                  (e.g. a match verified before this check existed, or a
                  study's disease changed to Diagnostics later). */}
              {returnDueFor(studyEnrollments).due && (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-teal-400/30 bg-teal-400/10 px-2.5 py-2 text-[11px] text-teal-100">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  <span className="flex-1 min-w-[12rem]">
                    {RETURN_REASON_TEXT[returnDueFor(studyEnrollments).outcome.reason]}, so this patient can go back to{" "}
                    {REFERRALS_STATUS} to be considered for other studies.
                  </span>
                  <button
                    type="button"
                    onClick={() => applyReturnIfDue(studyEnrollments)}
                    className="enroll-solid-btn inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-teal-500 to-emerald-500 px-2.5 py-1 text-[11px] font-semibold text-white"
                  >
                    Move to {REFERRALS_STATUS}
                  </button>
                </div>
              )}

              {returnedToReferralsEntry && status === REFERRALS_STATUS && (
                <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-teal-400/30 bg-teal-400/10 px-2.5 py-1.5 text-[11px] text-teal-100">
                  <ArrowLeft className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    Moved back to {REFERRALS_STATUS}
                    {returnedToReferralsEntry.returnedAt
                      ? ` on ${new Date(returnedToReferralsEntry.returnedAt).toLocaleDateString()}`
                      : ""}
                    : {RETURN_REASON_TEXT[returnedToReferralsEntry.returnedReason] || "all studies are done"}. They
                    can be considered for other studies.
                  </span>
                </div>
              )}

              {studyEnrollments
                .filter((e) => e.approval === "rejected")
                .map((e) => (
                  <p
                    key={`rej-${entryKey(e)}`}
                    className="mt-2 flex items-start gap-1.5 rounded-lg border border-rose-400/30 bg-rose-400/10 px-2.5 py-1.5 text-[11px] text-rose-200"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      An admin rejected {e.studyName || e.studyId}
                      {e.rejectedAt ? ` on ${new Date(e.rejectedAt).toLocaleDateString()}` : ""}
                      {e.rejectedReason ? `: ${e.rejectedReason}` : ""}. It no longer counts as an enrollment.
                    </span>
                  </p>
                ))}

              {enrollmentView.past.length > 0 && (
                <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">Previously enrolled</p>
                  <ul className="space-y-1.5">
                    {enrollmentView.past.map((e) => {
                      const s = studiesById.get(e.studyId);
                      const name = e.studyName || studyDisplayName(s) || e.studyId;
                      return (
                        <li key={entryKey(e)} className="text-xs text-slate-300 leading-relaxed">
                          This patient was enrolled in <span className="font-semibold text-white">{name}</span>.{" "}
                          {e.closedAt
                            ? `The study closed on ${new Date(e.closedAt).toLocaleDateString()}`
                            : "The study has closed"}
                          {e.returnedAt
                            ? `, so they were moved back to ${REFERRALS_STATUS} to be considered for other studies.`
                            : "."}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {activeEnrollmentCount === 0 && (enrolledStudiesLoading || enrolledStudies.length > 0) && (
                <div className="mt-2">
                  <div className="mb-2 flex items-center gap-1.5 rounded-lg border border-amber-400/30 bg-amber-400/10 px-2.5 py-1.5 text-[11px] font-medium text-amber-200">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    Possible matches on the study-patients list, by name only. Not saved. Use Add study to record one.
                  </div>
                  {enrolledStudiesLoading ? (
                    <div className="py-3 text-center text-xs text-slate-400">
                      Checking enrollment across studies…
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {enrolledStudies.map((s) => (
                        <span
                          key={s.id}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.05] px-2.5 py-1.5 text-xs font-medium text-slate-200"
                        >
                          <Stethoscope className="w-3 h-3 text-slate-400 shrink-0" />
                          <span className="truncate">{s.label}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {isEnrolledStatus(status) && activeEnrollmentCount === 0 && (
                <p className="mt-2 text-[11px] text-amber-200">
                  No study recorded for this patient yet.
                </p>
              )}

              <button
                type="button"
                onClick={() => setShowEnrollPicker(true)}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-emerald-300/40 bg-emerald-500/15 px-3 py-1.5 text-xs font-semibold text-emerald-100 hover:bg-emerald-500/25 transition"
              >
                <Plus className="w-3.5 h-3.5" />
                Add study
              </button>
            </Section>
          )}

          {/* Potential Studies */}
          <Section
            id="sec-potential"
            title="Potential Studies"
            subtitle="Studies the patient may qualify for"
            icon={<Stethoscope className="w-3.5 h-3.5 text-purple-300" />}
            defaultOpen={false}
            headerExtra={
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-purple-500/15 border border-purple-300/30 text-[10px] font-semibold text-purple-200">
                {potentialStudyIds.length} selected
              </span>
            }
          >
            <div className="space-y-2">
              {/* keep legacy state in sync for any external readers */}
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={studySearch}
                  onChange={(e) => {
                    setStudySearch(e.target.value);
                    setShowPotentialStudies(true);
                  }}
                  placeholder="Search studies by name or ID…"
                  className={`${inputCls} pl-9`}
                />
              </div>
              {/* Checkbox list */}
              <div className="grid gap-1.5 sm:grid-cols-2 max-h-72 overflow-y-auto pr-1 rounded-xl border border-white/10 bg-white/[0.02] p-2 scrollbar-thin">
                {allStudies
                  .filter((s) => !s.merged || potentialStudyIds.includes(s.id))
                  .filter((s) =>
                    (s.label || "")
                      .toLowerCase()
                      .includes(studySearch.trim().toLowerCase())
                  )
                  .map((s) => {
                    const checked = potentialStudyIds.includes(s.id);
                    return (
                      <label
                        key={s.id}
                        className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs cursor-pointer transition ${
                          checked
                            ? "bg-purple-500/15 border-purple-300/30 text-white"
                            : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06] hover:border-purple-300/20"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPotentialStudyIds([
                                ...potentialStudyIds,
                                s.id,
                              ]);
                            } else {
                              setPotentialStudyIds(
                                potentialStudyIds.filter((id) => id !== s.id)
                              );
                            }
                          }}
                          className="w-3.5 h-3.5 accent-purple-500 shrink-0"
                        />
                        <span className="truncate">{s.label}</span>
                      </label>
                    );
                  })}
                {allStudies.length === 0 && (
                  <div className="sm:col-span-2 py-4 text-center text-xs text-slate-500">
                    No studies loaded.
                  </div>
                )}
              </div>
            </div>
          </Section>

          {/* Call / Scheduling */}
          <Section
            id="sec-scheduling"
            title="Call / Scheduling"
            subtitle="Outreach and appointment"
            icon={<Calendar className="w-3.5 h-3.5 text-purple-300" />}
            defaultOpen={false}
          >
            <div className="space-y-3">
              <div>
                <span className={labelCls}>Call Made?</span>
                <div className="inline-flex p-1 rounded-xl bg-white/[0.04] border border-white/10 gap-0.5">
                  {[
                    [true, "Yes"],
                    [false, "No"],
                  ].map(([val, label]) => (
                    <button
                      key={String(val)}
                      type="button"
                      onClick={() => setCallMade(val)}
                      className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition ${
                        callMade === val
                          ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-[0_4px_12px_-2px_rgba(139,92,246,0.5)]"
                          : "text-slate-300 hover:text-white"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {callMade && (
                <>
                  <div>
                    <span className={labelCls}>Patient Scheduled?</span>
                    <div className="inline-flex p-1 rounded-xl bg-white/[0.04] border border-white/10 gap-0.5">
                      {[
                        [true, "Yes"],
                        [false, "No"],
                      ].map(([val, label]) => (
                        <button
                          key={String(val)}
                          type="button"
                          onClick={() => setScheduled(val)}
                          className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition ${
                            scheduled === val
                              ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-[0_4px_12px_-2px_rgba(139,92,246,0.5)]"
                              : "text-slate-300 hover:text-white"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {scheduled && (
                    <div className="grid sm:grid-cols-2 gap-3 items-end">
                      <label className="block">
                        <span className={labelCls}>Date Scheduled</span>
                        <div className="relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                          <input
                            type="date"
                            className={`${inputDateCls} pl-9`}
                            value={dateScheduled}
                            onChange={(e) => setDateScheduled(e.target.value)}
                          />
                        </div>
                      </label>
                      <a
                        href="/research-calendar"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-purple-300/30 text-xs font-medium text-slate-200 hover:text-white transition"
                      >
                        View Schedule in RegX
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}

                  {/* Research-calendar scheduling (single calendar, room-as-metadata) */}
                  {scheduled && (
                    <div className="mt-1 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                      {calendarEventId ? (
                        <div className="flex items-start justify-between gap-3">
                          <div className="text-xs text-slate-300 space-y-0.5">
                            <div className="flex items-center gap-1.5 text-white font-semibold">
                              <Calendar className="w-3.5 h-3.5 text-purple-300" />
                              {apptRoom === "Other"
                                ? `Other: ${apptOtherRoom}`
                                : apptRoom || "Room"}
                              {apptStartTime && (
                                <span className="text-slate-400 font-normal">
                                  {apptStartTime}
                                  {apptEndTime ? `–${apptEndTime}` : ""}
                                </span>
                              )}
                            </div>
                            <div className="text-slate-400">
                              {[apptStudy, apptVisit, apptCoordinator]
                                .filter(Boolean)
                                .join(" · ") || "Scheduled on Research calendar"}
                            </div>
                            {calendarEventLink && (
                              <a
                                href={calendarEventLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-purple-300 hover:text-purple-200"
                              >
                                Open in Google Calendar
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                            {apptCreatedBy && (
                              <div className="text-[10px] text-slate-500">
                                Created by {apptCreatedBy.split("@")[0]}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowScheduleModal(true)}
                            className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-slate-200 hover:text-white bg-white/[0.04] border border-white/10 hover:border-purple-300/30 transition"
                          >
                            Edit
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowScheduleModal(true)}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-purple-500 to-blue-500 shadow-[0_4px_12px_-2px_rgba(139,92,246,0.5)] hover:brightness-110 transition"
                        >
                          <Calendar className="w-3.5 h-3.5" />
                          Add to Research Calendar
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </Section>

          {/* Status & Diagnosis */}
          <Section
            id="sec-status"
            title="Status & Diagnosis"
            subtitle="Final status and conditions"
            icon={<Activity className="w-3.5 h-3.5 text-purple-300" />}
            accent="from-rose-400 via-purple-400 to-blue-400"
            defaultOpen={false}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left column: urgent + status */}
              <div className="space-y-3">
                {/* Urgent toggle */}
                <div
                  className={`rounded-xl border p-3 transition ${
                    isUrgent
                      ? "bg-rose-500/10 border-rose-400/30 shadow-[0_0_20px_-8px_rgba(244,63,94,0.5)]"
                      : "bg-white/[0.03] border-white/10"
                  }`}
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isUrgent}
                      onChange={(e) => {
                        setIsUrgent(e.target.checked);
                        if (e.target.checked && !isUrgent) {
                          setUrgentTimestamp(new Date().toISOString());
                        } else {
                          setUrgentReason("");
                          setUrgentTimestamp(null);
                        }
                      }}
                      className="w-4 h-4 accent-rose-500"
                    />
                    <Flame className="w-4 h-4 text-rose-300" />
                    <span className="text-sm font-semibold text-white">
                      Mark as Urgent
                    </span>
                  </label>
                  {isUrgent && (
                    <textarea
                      placeholder="Why is this urgent?"
                      value={urgentReason}
                      onChange={(e) => setUrgentReason(e.target.value)}
                      className={`${inputCls} mt-2 resize-y min-h-[60px]`}
                    />
                  )}
                </div>

                {/* On-hold toggle (formerly "Awaiting LDCT Scan" — generalized to any reason a coordinator is waiting on a patient) */}
                <div
                  className={`rounded-xl border p-3 transition ${
                    onHold
                      ? "bg-sky-500/15 border-sky-400/30 shadow-[0_0_20px_-8px_rgba(56,189,248,0.5)]"
                      : "bg-white/[0.03] border-white/10"
                  }`}
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={onHold}
                      onChange={(e) => setOnHold(e.target.checked)}
                      className="w-4 h-4 accent-sky-500"
                    />
                    <Hourglass className="w-4 h-4 text-sky-300" />
                    <span className="text-sm font-semibold text-white">
                      On Hold
                    </span>
                  </label>
                  {onHold && (
                    <p className="mt-1.5 text-xs text-slate-400">
                      Excluded from the stale coordinator-box reminder while checked.
                    </p>
                  )}
                </div>

                {/* Enrolled in Diagnostics — derived from the recorded studies,
                    so it clears on its own when the diagnostics study closes
                    or is removed. Only a patient flagged by the old pop-up
                    (no study recorded) keeps the untick-to-clear checkbox,
                    since there is no study to remove. */}
                {diagBadge.show && (
                  <div className="rounded-xl border p-3 transition bg-emerald-500/15 border-emerald-400/30 shadow-[0_0_20px_-8px_rgba(52,211,153,0.5)]">
                    {diagBadge.legacy ? (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enrolledInDiagnostics}
                          onChange={(e) => {
                            if (!e.target.checked) {
                              setEnrolledInDiagnostics(false);
                              setEnrolledInDiagnosticsAt(null);
                              setEnrolledInDiagnosticsBy(null);
                            }
                          }}
                          className="w-4 h-4 accent-emerald-500"
                        />
                        <Stethoscope className="w-4 h-4 text-emerald-300" />
                        <span className="text-sm font-semibold text-white">Enrolled in Diagnostics</span>
                      </label>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Stethoscope className="w-4 h-4 text-emerald-300" />
                        <span className="text-sm font-semibold text-white">Enrolled in Diagnostics</span>
                      </div>
                    )}
                    <p className="mt-1.5 text-xs text-slate-400">
                      {diagBadge.legacy ? (
                        <>
                          Marked as enrolled in a diagnostics study
                          {enrolledInDiagnosticsAt
                            ? ` on ${new Date(enrolledInDiagnosticsAt).toLocaleDateString()}`
                            : ""}
                          {enrolledInDiagnosticsBy ? ` by ${enrolledInDiagnosticsBy}` : ""}, but no study was recorded.
                          Add the study under Enrolled Studies, or untick and Save to clear.
                        </>
                      ) : (
                        <>
                          Enrolled in {diagStudyNames}. Still eligible for other studies. This follows the patient's
                          Enrolled Studies and clears when the study closes or is removed there.
                        </>
                      )}
                    </p>
                  </div>
                )}

                <div className="block">
                  <span className={labelCls}>Final Status</span>
                  <StatusPicker
                    value={status}
                    onChange={(val) => {
                      // Picking Enrolled asks which study first; the pop-up
                      // then applies the status (Enrolled, or back to
                      // referrals when every study is diagnostics).
                      if (val === "Enrolled" && status !== "Enrolled") {
                        setShowEnrollPicker(true);
                        return;
                      }
                      setStatus(val);
                      if (val !== "Rescreen") {
                        setRescreenDate("");
                      }
                    }}
                    coordinators={coordinators}
                    baseStatuses={baseStatuses}
                  />
                  {status === "Rescreen" && (
                    <div className="mt-2">
                      <span className={labelCls}>
                        Rescreen Date{" "}
                        <span className="text-rose-400">*</span>
                      </span>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                        <input
                          type="date"
                          className={`${inputDateCls} pl-9`}
                          value={rescreenDate}
                          onChange={(e) => setRescreenDate(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right column: diagnosis pills */}
              <div>
                <span className={labelCls}>Diagnosis</span>
                <div className="flex flex-wrap gap-1.5">
                  {diagnosisOptions.map((d) => {
                    const active = diagnosis.includes(d);
                    return (
                      <button
                        type="button"
                        key={d}
                        onClick={() => {
                          if (active) {
                            setDiagnosis((prev) =>
                              prev.filter((x) => x !== d)
                            );
                          } else {
                            setDiagnosis((prev) => [...prev, d]);
                          }
                        }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          active
                            ? "bg-gradient-to-r from-purple-500/30 to-blue-500/30 border-purple-300/40 text-white shadow-[0_0_15px_-2px_rgba(139,92,246,0.5)]"
                            : "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/[0.08] hover:border-purple-300/30 hover:text-white"
                        }`}
                      >
                        {active ? (
                          <CheckCircle2 className="w-3 h-3 text-emerald-300" />
                        ) : (
                          <span className="w-3 h-3 rounded-full border border-white/20" />
                        )}
                        <HeartPulse className="w-3 h-3 text-purple-300" />
                        {diagnosisLabel(d)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </Section>

          {/* ---------- PFT (always visible; flag PFT_HISTORY_ENABLED) ---------- */}
          {PFT_HISTORY_ENABLED && (
            <Section
              id="sec-pft"
              title="Pulmonary Function Tests (PFT)"
              subtitle={(() => {
                const n = unverifiedSoftChecks(pftTests, { patientAddress: physicalAddress, patientDob: dob });
                return n ? `Every PFT on record, by date · ${n} to verify against the PDF` : "Every PFT on record, by date";
              })()}
              icon={<HeartPulse className="w-3.5 h-3.5 text-purple-300" />}
              accent="from-cyan-400 via-purple-400 to-blue-400"
              defaultOpen={false}
            >
              <PftHistoryEditor
                tests={pftTests}
                onChange={setPftTests}
                pftNA={pftNA}
                naToggle={<NaToggle checked={pftNA} onChange={() => setPftNA(!pftNA)} />}
                userEmail={auth.currentUser?.email || ""}
                inputCls={inputCls}
                inputDateCls={inputDateCls}
                labelCls={labelCls}
                onError={(msg) => setToast({ type: "error", msg })}
                // Every PFT date starts closed (one-line summary); staff open what they need.
                startCollapsed
                softChecks
                patientAddress={physicalAddress}
                patientDob={dob}
              />
            </Section>
          )}

          {/* ---------- Asthma Clinical Data ---------- */}
          {diagnosis.includes("Asthma") && (
            <Section
              id="sec-asthma"
              title="Asthma Clinical Data"
              subtitle="Eligibility-driving fields"
              icon={<Wind className="w-3.5 h-3.5 text-purple-300" />}
              accent="from-cyan-400 via-purple-400 to-blue-400"
              defaultOpen={false}
            >
              <div className="space-y-3">
                {/* EOS */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      EOS
                    </span>
                    <NaToggle checked={eosNA} onChange={() => setEosNA(!eosNA)} />
                  </div>
                  {!eosNA && (
                    <div className="space-y-2">
                      {eosValues.map((e, i) => (
                        <div key={i} className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            value={e.value}
                            onChange={(ev) => {
                              const arr = [...eosValues];
                              arr[i].value = ev.target.value;
                              setEosValues(arr);
                            }}
                            placeholder="EOS"
                            className={inputCls}
                          />
                          <input
                            type="date"
                            value={e.date}
                            onChange={(ev) => {
                              const arr = [...eosValues];
                              arr[i].date = ev.target.value;
                              setEosValues(arr);
                            }}
                            className={inputDateCls}
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setEosValues([...eosValues, { value: "", date: "" }])
                        }
                        className="inline-flex items-center gap-1 text-xs font-medium text-purple-300 hover:text-white transition"
                      >
                        <Plus className="w-3 h-3" />
                        Add EOS
                      </button>
                    </div>
                  )}
                </div>

                {/* Exacerbations */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Exacerbations (last year)
                    </span>
                    <NaToggle checked={exacNA} onChange={() => setExacNA(!exacNA)} />
                  </div>
                  {!exacNA && (
                    <div className="space-y-2">
                      {exacerbations.map((e, i) => (
                        <div key={i} className="grid grid-cols-2 gap-2">
                          <input
                            type="date"
                            value={e.date}
                            onChange={(ev) => {
                              const arr = [...exacerbations];
                              arr[i].date = ev.target.value;
                              setExacerbations(arr);
                            }}
                            className={inputDateCls}
                          />
                          <div className="relative">
                            <select
                              value={e.type}
                              onChange={(ev) => {
                                const arr = [...exacerbations];
                                arr[i].type = ev.target.value;
                                setExacerbations(arr);
                              }}
                              className={selectCls}
                            >
                              <option className="bg-[#0b0820] text-slate-100">
                                Outpatient
                              </option>
                              <option className="bg-[#0b0820] text-slate-100">
                                Hospital
                              </option>
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setExacerbations([
                            ...exacerbations,
                            { date: "", type: "Outpatient" },
                          ])
                        }
                        className="inline-flex items-center gap-1 text-xs font-medium text-purple-300 hover:text-white transition"
                      >
                        <Plus className="w-3 h-3" />
                        Add Exacerbation
                      </button>
                    </div>
                  )}
                </div>

                {/* Age */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Age
                    </span>
                    <NaToggle checked={ageNA} onChange={() => setAgeNA(!ageNA)} />
                  </div>
                  {!ageNA && (
                    <input
                      type="number"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      className={inputCls}
                    />
                  )}
                </div>

                {/* PFT (moved to its own section when PFT_HISTORY_ENABLED) */}
                {!PFT_HISTORY_ENABLED && (
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      PFT
                    </span>
                    <NaToggle checked={pftNA} onChange={() => setPftNA(!pftNA)} />
                  </div>
                  {!pftNA && (
                    <div className="space-y-3">
                      <label className="block">
                        <span className={labelCls}>PFT Date</span>
                        <input
                          type="date"
                          value={pftDate}
                          onChange={(e) => setPftDate(e.target.value)}
                          className={inputDateCls}
                        />
                      </label>

                      <div>
                        <span className={labelCls}>FEV1/FVC Ratio (Actual)</span>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="block">
                            <span className="text-[10px] text-slate-500">Pre</span>
                            <input
                              type="number"
                              value={fev1fvcPre}
                              onChange={(e) => setFev1fvcPre(e.target.value)}
                              className={inputCls}
                            />
                          </label>
                          <label className="block">
                            <span className="text-[10px] text-slate-500">Post</span>
                            <input
                              type="number"
                              value={fev1fvcPost}
                              onChange={(e) => setFev1fvcPost(e.target.value)}
                              className={inputCls}
                            />
                          </label>
                        </div>
                      </div>

                      <div>
                        <span className={labelCls}>FEV1 % Predicted</span>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="block">
                            <span className="text-[10px] text-slate-500">Pre</span>
                            <input
                              type="number"
                              value={fev1Pre}
                              onChange={(e) => setFev1Pre(e.target.value)}
                              className={inputCls}
                            />
                          </label>
                          <label className="block">
                            <span className="text-[10px] text-slate-500">Post</span>
                            <input
                              type="number"
                              value={fev1Post}
                              onChange={(e) => setFev1Post(e.target.value)}
                              className={inputCls}
                            />
                          </label>
                        </div>
                      </div>

                      <div>
                        <span className={labelCls}>FVC % Predicted</span>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="block">
                            <span className="text-[10px] text-slate-500">Pre</span>
                            <input
                              type="number"
                              value={fvcPre}
                              onChange={(e) => setFvcPre(e.target.value)}
                              className={inputCls}
                            />
                          </label>
                          <label className="block">
                            <span className="text-[10px] text-slate-500">Post</span>
                            <input
                              type="number"
                              value={fvcPost}
                              onChange={(e) => setFvcPost(e.target.value)}
                              className={inputCls}
                            />
                          </label>
                        </div>
                      </div>

                      <div>
                        <span className={labelCls}>FEV1 Reversibility</span>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={revPercent}
                              onChange={(e) => setRevPercent(e.target.value)}
                              className={inputCls}
                            />
                            <span className="text-xs text-slate-400 shrink-0">% change</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={revMl}
                              onChange={(e) => setRevMl(e.target.value)}
                              className={inputCls}
                            />
                            <span className="text-xs text-slate-400 shrink-0">ml</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* FENO */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      FENO
                    </span>
                    <NaToggle checked={fenoNA} onChange={() => setFenoNA(!fenoNA)} />
                  </div>
                  {!fenoNA && (
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        placeholder="PPb"
                        value={fenoPPB}
                        onChange={(e) => setFenoPPB(e.target.value)}
                        className={inputCls}
                      />
                      <input
                        type="date"
                        value={fenoDate}
                        onChange={(e) => setFenoDate(e.target.value)}
                        className={inputDateCls}
                      />
                    </div>
                  )}
                </div>

                {/* Methacholine */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Methacholine Challenge
                    </span>
                    <NaToggle checked={methNA} onChange={() => setMethNA(!methNA)} />
                  </div>
                  {!methNA && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <select
                          value={methResult}
                          onChange={(e) => setMethResult(e.target.value)}
                          className={selectCls}
                        >
                          <option value="" className="bg-[#0b0820] text-slate-100">
                            Result
                          </option>
                          <option className="bg-[#0b0820] text-slate-100">Positive</option>
                          <option className="bg-[#0b0820] text-slate-100">Negative</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                      <input
                        type="date"
                        value={methDate}
                        onChange={(e) => setMethDate(e.target.value)}
                        className={inputDateCls}
                      />
                    </div>
                  )}
                </div>

                {/* Cancer */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Cancer (except Basal/Squamous Cell Skin Cancer, last 5 yrs)
                    </span>
                    <NaToggle checked={cancerNA} onChange={() => setCancerNA(!cancerNA)} />
                  </div>
                  {!cancerNA && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <select
                          value={cancerStatus}
                          onChange={(e) => setCancerStatus(e.target.value)}
                          className={selectCls}
                        >
                          <option value="" className="bg-[#0b0820] text-slate-100">
                            Select
                          </option>
                          <option className="bg-[#0b0820] text-slate-100">Yes</option>
                          <option className="bg-[#0b0820] text-slate-100">No</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                      <input
                        type="date"
                        value={cancerDate}
                        onChange={(e) => setCancerDate(e.target.value)}
                        className={inputDateCls}
                      />
                    </div>
                  )}
                </div>
              </div>
            </Section>
          )}

          {/* ---------- COPD Clinical Data ---------- */}
          {diagnosis.includes("COPD") && (
            <Section
              id="sec-copd"
              title="COPD Clinical Data"
              subtitle="Eligibility-driving fields"
              icon={<Cigarette className="w-3.5 h-3.5 text-purple-300" />}
              accent="from-amber-400 via-purple-400 to-blue-400"
              defaultOpen={false}
            >
              <div className="space-y-3">
                {/* EOS */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      EOS
                    </span>
                    <NaToggle checked={eosNA} onChange={() => setEosNA(!eosNA)} />
                  </div>
                  {!eosNA && (
                    <div className="space-y-2">
                      {eosValues.map((e, i) => (
                        <div key={i} className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            value={e.value}
                            onChange={(ev) => {
                              const arr = [...eosValues];
                              arr[i].value = ev.target.value;
                              setEosValues(arr);
                            }}
                            placeholder="EOS"
                            className={inputCls}
                          />
                          <input
                            type="date"
                            value={e.date}
                            onChange={(ev) => {
                              const arr = [...eosValues];
                              arr[i].date = ev.target.value;
                              setEosValues(arr);
                            }}
                            className={inputDateCls}
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setEosValues([...eosValues, { value: "", date: "" }])
                        }
                        className="inline-flex items-center gap-1 text-xs font-medium text-purple-300 hover:text-white transition"
                      >
                        <Plus className="w-3 h-3" />
                        Add EOS
                      </button>
                    </div>
                  )}
                </div>

                {/* Exacerbations */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Exacerbations (last year)
                    </span>
                    <NaToggle checked={exacNA} onChange={() => setExacNA(!exacNA)} />
                  </div>
                  {!exacNA && (
                    <div className="space-y-2">
                      {exacerbations.map((e, i) => (
                        <div key={i} className="grid grid-cols-2 gap-2">
                          <input
                            type="date"
                            value={e.date}
                            onChange={(ev) => {
                              const arr = [...exacerbations];
                              arr[i].date = ev.target.value;
                              setExacerbations(arr);
                            }}
                            className={inputDateCls}
                          />
                          <div className="relative">
                            <select
                              value={e.type}
                              onChange={(ev) => {
                                const arr = [...exacerbations];
                                arr[i].type = ev.target.value;
                                setExacerbations(arr);
                              }}
                              className={selectCls}
                            >
                              <option className="bg-[#0b0820] text-slate-100">
                                Outpatient
                              </option>
                              <option className="bg-[#0b0820] text-slate-100">
                                Hospital
                              </option>
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setExacerbations([
                            ...exacerbations,
                            { date: "", type: "Outpatient" },
                          ])
                        }
                        className="inline-flex items-center gap-1 text-xs font-medium text-purple-300 hover:text-white transition"
                      >
                        <Plus className="w-3 h-3" />
                        Add Exacerbation
                      </button>
                    </div>
                  )}
                </div>

                {/* Age */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Age
                    </span>
                    <NaToggle checked={ageNA} onChange={() => setAgeNA(!ageNA)} />
                  </div>
                  {!ageNA && (
                    <input
                      type="number"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      className={inputCls}
                    />
                  )}
                </div>

                {/* PFT (moved to its own section when PFT_HISTORY_ENABLED) */}
                {!PFT_HISTORY_ENABLED && (
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Pulmonary Function Test (PFT)
                    </span>
                    <NaToggle checked={pftNA} onChange={() => setPftNA(!pftNA)} />
                  </div>
                  {!pftNA && (
                    <div className="space-y-3">
                      <label className="block">
                        <span className={labelCls}>PFT Date</span>
                        <input
                          type="date"
                          value={pftDate}
                          onChange={(e) => setPftDate(e.target.value)}
                          className={inputDateCls}
                        />
                      </label>

                      <div>
                        <span className={labelCls}>FEV1/FVC Ratio (Actual)</span>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="block">
                            <span className="text-[10px] text-slate-500">Pre</span>
                            <input
                              type="number"
                              value={fev1fvcPre}
                              onChange={(e) => setFev1fvcPre(e.target.value)}
                              className={inputCls}
                            />
                          </label>
                          <label className="block">
                            <span className="text-[10px] text-slate-500">Post</span>
                            <input
                              type="number"
                              value={fev1fvcPost}
                              onChange={(e) => setFev1fvcPost(e.target.value)}
                              className={inputCls}
                            />
                          </label>
                        </div>
                      </div>

                      <div>
                        <span className={labelCls}>FEV1 % Predicted</span>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            placeholder="Pre"
                            value={fev1Pre}
                            onChange={(e) => setFev1Pre(e.target.value)}
                            className={inputCls}
                          />
                          <input
                            type="number"
                            placeholder="Post"
                            value={fev1Post}
                            onChange={(e) => setFev1Post(e.target.value)}
                            className={inputCls}
                          />
                        </div>
                      </div>

                      <div>
                        <span className={labelCls}>FVC % Predicted</span>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            placeholder="Pre"
                            value={fvcPre}
                            onChange={(e) => setFvcPre(e.target.value)}
                            className={inputCls}
                          />
                          <input
                            type="number"
                            placeholder="Post"
                            value={fvcPost}
                            onChange={(e) => setFvcPost(e.target.value)}
                            className={inputCls}
                          />
                        </div>
                      </div>

                      <div>
                        <span className={labelCls}>FEV1 Reversibility</span>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={revPercent}
                              onChange={(e) => setRevPercent(e.target.value)}
                              className={inputCls}
                            />
                            <span className="text-xs text-slate-400 shrink-0">% change</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={revMl}
                              onChange={(e) => setRevMl(e.target.value)}
                              className={inputCls}
                            />
                            <span className="text-xs text-slate-400 shrink-0">ml</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* FENO */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      FENO
                    </span>
                    <NaToggle checked={fenoNA} onChange={() => setFenoNA(!fenoNA)} />
                  </div>
                  {!fenoNA && (
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        placeholder="PPb"
                        value={fenoPPB}
                        onChange={(e) => setFenoPPB(e.target.value)}
                        className={inputCls}
                      />
                      <input
                        type="date"
                        value={fenoDate}
                        onChange={(e) => setFenoDate(e.target.value)}
                        className={inputDateCls}
                      />
                    </div>
                  )}
                </div>

                {/* Oxygen */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Oxygen
                    </span>
                    <NaToggle checked={oxygenNA} onChange={() => setOxygenNA(!oxygenNA)} />
                  </div>
                  {!oxygenNA && (
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        value={oxygenLitres}
                        onChange={(e) => setOxygenLitres(e.target.value)}
                        placeholder="Litres"
                        className={inputCls}
                      />
                      <div className="relative">
                        <select
                          value={oxygenType}
                          onChange={(e) => setOxygenType(e.target.value)}
                          className={selectCls}
                        >
                          <option className="bg-[#0b0820] text-slate-100">PRN</option>
                          <option className="bg-[#0b0820] text-slate-100">Night</option>
                          <option className="bg-[#0b0820] text-slate-100">Continuous</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Cancer */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Cancer (except Basal/Squamous Cell Skin Cancer, last 5 yrs)
                    </span>
                    <NaToggle checked={cancerNA} onChange={() => setCancerNA(!cancerNA)} />
                  </div>
                  {!cancerNA && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <select
                          value={cancerStatus}
                          onChange={(e) => setCancerStatus(e.target.value)}
                          className={selectCls}
                        >
                          <option value="" className="bg-[#0b0820] text-slate-100">
                            Select
                          </option>
                          <option className="bg-[#0b0820] text-slate-100">Yes</option>
                          <option className="bg-[#0b0820] text-slate-100">No</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                      <input
                        type="date"
                        value={cancerDate}
                        onChange={(e) => setCancerDate(e.target.value)}
                        className={inputDateCls}
                      />
                    </div>
                  )}
                </div>
              </div>
            </Section>
          )}

          {/* ---------- Bronchiectasis Clinical Data ---------- */}
          {diagnosis.includes("Bronchiectasis") && (
            <Section
              id="sec-bronch"
              title="Bronchiectasis Clinical Data"
              subtitle="Eligibility-driving fields"
              icon={<AirVent className="w-3.5 h-3.5 text-purple-300" />}
              accent="from-emerald-400 via-purple-400 to-blue-400"
              defaultOpen={false}
            >
              <div className="space-y-3">
                {/* EOS */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      EOS
                    </span>
                    <NaToggle checked={eosNA} onChange={() => setEosNA(!eosNA)} />
                  </div>
                  {!eosNA && (
                    <div className="space-y-2">
                      {eosValues.map((e, i) => (
                        <div key={i} className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            value={e.value}
                            onChange={(ev) => {
                              const arr = [...eosValues];
                              arr[i].value = ev.target.value;
                              setEosValues(arr);
                            }}
                            placeholder="EOS"
                            className={inputCls}
                          />
                          <input
                            type="date"
                            value={e.date}
                            onChange={(ev) => {
                              const arr = [...eosValues];
                              arr[i].date = ev.target.value;
                              setEosValues(arr);
                            }}
                            className={inputDateCls}
                          />
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setEosValues([...eosValues, { value: "", date: "" }])
                        }
                        className="inline-flex items-center gap-1 text-xs font-medium text-purple-300 hover:text-white transition"
                      >
                        <Plus className="w-3 h-3" />
                        Add EOS
                      </button>
                    </div>
                  )}
                </div>

                {/* Exacerbations */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Exacerbations (last year)
                    </span>
                    <NaToggle checked={exacNA} onChange={() => setExacNA(!exacNA)} />
                  </div>
                  {!exacNA && (
                    <div className="space-y-2">
                      {exacerbations.map((e, i) => (
                        <div key={i} className="grid grid-cols-2 gap-2">
                          <input
                            type="date"
                            value={e.date}
                            onChange={(ev) => {
                              const arr = [...exacerbations];
                              arr[i].date = ev.target.value;
                              setExacerbations(arr);
                            }}
                            className={inputDateCls}
                          />
                          <div className="relative">
                            <select
                              value={e.type}
                              onChange={(ev) => {
                                const arr = [...exacerbations];
                                arr[i].type = ev.target.value;
                                setExacerbations(arr);
                              }}
                              className={selectCls}
                            >
                              <option className="bg-[#0b0820] text-slate-100">
                                Outpatient
                              </option>
                              <option className="bg-[#0b0820] text-slate-100">
                                Hospital
                              </option>
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setExacerbations([
                            ...exacerbations,
                            { date: "", type: "Outpatient" },
                          ])
                        }
                        className="inline-flex items-center gap-1 text-xs font-medium text-purple-300 hover:text-white transition"
                      >
                        <Plus className="w-3 h-3" />
                        Add Exacerbation
                      </button>
                    </div>
                  )}
                </div>

                {/* Age */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Age
                    </span>
                    <NaToggle checked={ageNA} onChange={() => setAgeNA(!ageNA)} />
                  </div>
                  {!ageNA && (
                    <input
                      type="number"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      className={inputCls}
                    />
                  )}
                </div>

                {/* PFT (moved to its own section when PFT_HISTORY_ENABLED) */}
                {!PFT_HISTORY_ENABLED && (
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Pulmonary Function Test (PFT)
                    </span>
                    <NaToggle checked={pftNA} onChange={() => setPftNA(!pftNA)} />
                  </div>
                  {!pftNA && (
                    <div className="space-y-3">
                      <label className="block">
                        <span className={labelCls}>PFT Date</span>
                        <input
                          type="date"
                          value={pftDate}
                          onChange={(e) => setPftDate(e.target.value)}
                          className={inputDateCls}
                        />
                      </label>

                      <div>
                        <span className={labelCls}>FEV1/FVC Ratio (Actual)</span>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="block">
                            <span className="text-[10px] text-slate-500">Pre</span>
                            <input
                              type="number"
                              value={fev1fvcPre}
                              onChange={(e) => setFev1fvcPre(e.target.value)}
                              className={inputCls}
                            />
                          </label>
                          <label className="block">
                            <span className="text-[10px] text-slate-500">Post</span>
                            <input
                              type="number"
                              value={fev1fvcPost}
                              onChange={(e) => setFev1fvcPost(e.target.value)}
                              className={inputCls}
                            />
                          </label>
                        </div>
                      </div>

                      <div>
                        <span className={labelCls}>FEV1 % Predicted</span>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            placeholder="Pre"
                            value={fev1Pre}
                            onChange={(e) => setFev1Pre(e.target.value)}
                            className={inputCls}
                          />
                          <input
                            type="number"
                            placeholder="Post"
                            value={fev1Post}
                            onChange={(e) => setFev1Post(e.target.value)}
                            className={inputCls}
                          />
                        </div>
                      </div>

                      <div>
                        <span className={labelCls}>FVC % Predicted</span>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="number"
                            placeholder="Pre"
                            value={fvcPre}
                            onChange={(e) => setFvcPre(e.target.value)}
                            className={inputCls}
                          />
                          <input
                            type="number"
                            placeholder="Post"
                            value={fvcPost}
                            onChange={(e) => setFvcPost(e.target.value)}
                            className={inputCls}
                          />
                        </div>
                      </div>

                      <div>
                        <span className={labelCls}>FEV1 Reversibility</span>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={revPercent}
                              onChange={(e) => setRevPercent(e.target.value)}
                              className={inputCls}
                            />
                            <span className="text-xs text-slate-400 shrink-0">% change</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={revMl}
                              onChange={(e) => setRevMl(e.target.value)}
                              className={inputCls}
                            />
                            <span className="text-xs text-slate-400 shrink-0">ml</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* FENO */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      FENO
                    </span>
                    <NaToggle checked={fenoNA} onChange={() => setFenoNA(!fenoNA)} />
                  </div>
                  {!fenoNA && (
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="number"
                        placeholder="PPb"
                        value={fenoPPB}
                        onChange={(e) => setFenoPPB(e.target.value)}
                        className={inputCls}
                      />
                      <input
                        type="date"
                        value={fenoDate}
                        onChange={(e) => setFenoDate(e.target.value)}
                        className={inputDateCls}
                      />
                    </div>
                  )}
                </div>

                {/* Cancer */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Cancer (except Basal/Squamous Cell Skin Cancer, last 5 yrs)
                    </span>
                    <NaToggle checked={cancerNA} onChange={() => setCancerNA(!cancerNA)} />
                  </div>
                  {!cancerNA && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <select
                          value={cancerStatus}
                          onChange={(e) => setCancerStatus(e.target.value)}
                          className={selectCls}
                        >
                          <option value="" className="bg-[#0b0820] text-slate-100">
                            Select
                          </option>
                          <option className="bg-[#0b0820] text-slate-100">Yes</option>
                          <option className="bg-[#0b0820] text-slate-100">No</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                      <input
                        type="date"
                        value={cancerDate}
                        onChange={(e) => setCancerDate(e.target.value)}
                        className={inputDateCls}
                      />
                    </div>
                  )}
                </div>
              </div>
            </Section>
          )}

          {/* ---------- Sleep Apnea Clinical Data ---------- */}
          {diagnosis.includes("Sleep Apnea") && (
            <Section
              id="sec-sleep"
              title="Sleep Apnea Clinical Data"
              subtitle="Eligibility-driving fields"
              icon={<Moon className="w-3.5 h-3.5 text-purple-300" />}
              accent="from-indigo-400 via-purple-400 to-blue-400"
              defaultOpen={false}
            >
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <label className="block">
                    <span className={labelCls}>BMI</span>
                    <input
                      type="number"
                      value={bmi}
                      onChange={(e) => setBmi(e.target.value)}
                      className={inputCls}
                    />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Age</span>
                    <input
                      type="number"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      className={inputCls}
                    />
                  </label>
                  <label className="block">
                    <span className={labelCls}>AHI</span>
                    <input
                      type="number"
                      value={ahi}
                      onChange={(e) => setAhi(e.target.value)}
                      className={inputCls}
                    />
                  </label>
                </div>

                {/* A1C */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <span className="text-xs font-semibold text-white uppercase tracking-wider">
                    A1C
                  </span>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <input
                      type="number"
                      value={a1c}
                      onChange={(e) => setA1c(e.target.value)}
                      className={inputCls}
                    />
                    <input
                      type="date"
                      value={a1cDate}
                      onChange={(e) => setA1cDate(e.target.value)}
                      className={inputDateCls}
                    />
                  </div>
                </div>

                {/* Diabetes */}
                <label className="block">
                  <span className={labelCls}>Diabetes</span>
                  <div className="relative">
                    <select
                      value={diabetes}
                      onChange={(e) => setDiabetes(e.target.value)}
                      className={selectCls}
                    >
                      <option value="" className="bg-[#0b0820] text-slate-100">
                        Select
                      </option>
                      <option className="bg-[#0b0820] text-slate-100">Yes</option>
                      <option className="bg-[#0b0820] text-slate-100">No</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  </div>
                </label>

                {/* Cancer */}
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Cancer
                    </span>
                    <NaToggle checked={cancerNA} onChange={() => setCancerNA(!cancerNA)} />
                  </div>
                  {!cancerNA && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="relative">
                        <select
                          value={cancerStatus}
                          onChange={(e) => setCancerStatus(e.target.value)}
                          className={selectCls}
                        >
                          <option value="" className="bg-[#0b0820] text-slate-100">
                            Select
                          </option>
                          <option className="bg-[#0b0820] text-slate-100">Yes</option>
                          <option className="bg-[#0b0820] text-slate-100">No</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      </div>
                      <input
                        type="date"
                        value={cancerDate}
                        onChange={(e) => setCancerDate(e.target.value)}
                        className={inputDateCls}
                      />
                    </div>
                  )}
                </div>
              </div>
            </Section>
          )}

          {/* ---------- Diagnostics Clinical Data ---------- */}
          {showClinicalDiagnostics && (
            <Section
              id="sec-diagnostics"
              title="Diagnostics Clinical Data"
              subtitle="Scans and nodules"
              icon={<ScanLine className="w-3.5 h-3.5 text-purple-300" />}
              accent="from-indigo-400 via-purple-400 to-blue-400"
              defaultOpen={false}
            >
              <div className="space-y-3">
                {diagnosticScans.map((scan, i) => (
                  <div
                    key={scan.id}
                    data-testid="diagnostic-scan"
                    className="rounded-xl border border-white/10 bg-white/[0.025] p-3 space-y-2"
                  >
                    <span className="text-xs font-semibold text-white uppercase tracking-wider">
                      Scan {i + 1}
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <label className="block">
                        <span className={labelCls}>Scan date</span>
                        <input
                          type="date"
                          value={scan.date}
                          onChange={(e) => updateScan(i, { date: e.target.value })}
                          className={inputDateCls}
                        />
                      </label>
                      <label className="block">
                        <span className={labelCls}>Type of scan</span>
                        <div className="relative">
                          <select
                            value={scan.type}
                            onChange={(e) => updateScan(i, { type: e.target.value })}
                            className={selectCls}
                          >
                            <option value="" className="bg-[#0b0820] text-slate-100">
                              Select
                            </option>
                            {SCAN_TYPES.map((t) => (
                              <option key={t} className="bg-[#0b0820] text-slate-100">
                                {t}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                        </div>
                      </label>
                      <label className="block">
                        <span className={labelCls}>Nodules present?</span>
                        <div className="relative">
                          <select
                            value={scan.nodulesPresent}
                            onChange={(e) => updateScan(i, { nodulesPresent: e.target.value })}
                            className={selectCls}
                          >
                            <option value="" className="bg-[#0b0820] text-slate-100">
                              Select
                            </option>
                            <option className="bg-[#0b0820] text-slate-100">Yes</option>
                            <option className="bg-[#0b0820] text-slate-100">No</option>
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                        </div>
                      </label>
                    </div>

                    {showNoduleDetails(scan) && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                        <label className="block">
                          <span className={labelCls}>Size of nodules (mm)</span>
                          <input
                            type="text"
                            value={scan.size}
                            onChange={(e) => updateScan(i, { size: e.target.value })}
                            className={inputCls}
                          />
                        </label>
                        <label className="block">
                          <span className={labelCls}>Describe nodule</span>
                          <div className="relative">
                            <select
                              value={scan.change}
                              onChange={(e) => updateScan(i, { change: e.target.value })}
                              className={selectCls}
                            >
                              <option value="" className="bg-[#0b0820] text-slate-100">
                                Select
                              </option>
                              {NODULE_CHANGES.map((c) => (
                                <option key={c} className="bg-[#0b0820] text-slate-100">
                                  {c}
                                </option>
                              ))}
                            </select>
                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                          </div>
                        </label>
                        <div className="pb-1.5">
                          <NaToggle
                            label="Spiculated"
                            checked={!!scan.spiculated}
                            onChange={() => updateScan(i, { spiculated: !scan.spiculated })}
                          />
                        </div>
                        {showPriorSize(scan) && (
                          <label className="block">
                            <span className={labelCls}>Prior size (mm)</span>
                            <input
                              type="text"
                              value={scan.priorSize}
                              onChange={(e) => updateScan(i, { priorSize: e.target.value })}
                              className={inputCls}
                            />
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setDiagnosticScans((list) => [...list, blankScan()])}
                  className="inline-flex items-center gap-1 text-xs font-medium text-purple-300 hover:text-white transition"
                >
                  <Plus className="w-3 h-3" />
                  Add another scan
                </button>
              </div>
            </Section>
          )}

          {/* ---------- Study Eligibility ---------- */}
          {activeStudies.length > 0 && (
            <Section
              id="sec-eligibility"
              title="Study Eligibility"
              subtitle={`${activeStudies.length} active studies matching diagnosis`}
              icon={<CheckCircle2 className="w-3.5 h-3.5 text-purple-300" />}
              accent="from-emerald-400 via-purple-400 to-blue-400"
              defaultOpen={false}
              headerExtra={
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-300/30 text-[10px] font-semibold text-emerald-200">
                  {Object.keys(studyEligibility).length} marked
                </span>
              }
            >
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={studyEligibilitySearch}
                    onChange={(e) => {
                      setStudyEligibilitySearch(e.target.value);
                      setShowEligibilityStudies(true);
                    }}
                    placeholder="Search studies…"
                    className={`${inputCls} pl-9`}
                  />
                </div>

                <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
                  <div className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 border-b border-white/5 sticky top-0 bg-slate-950/60 backdrop-blur-md">
                    <span>Study</span>
                    <span className="w-20 text-center text-emerald-300">Eligible</span>
                    <span className="w-28 sm:w-60 text-center text-rose-300">Not Eligible</span>
                  </div>
                  <div className="max-h-[620px] overflow-y-auto divide-y divide-white/5 scrollbar-thin">
                    {activeStudies
                      .filter((s) =>
                        (s.NAME || "")
                          .toLowerCase()
                          .includes(studyEligibilitySearch.toLowerCase())
                      )
                      .map((studyItem) => {
                        const entry = studyEligibility[studyItem.id] || {};
                        return (
                          <div
                            key={studyItem.id}
                            className="grid grid-cols-[1fr_auto_auto] gap-2 px-3 py-2 items-start hover:bg-white/[0.03] transition"
                          >
                            <div className="text-xs text-slate-200 min-w-0 pt-1">
                              {studyItem.NAME}
                            </div>
                            <div className="w-20 flex justify-center pt-1">
                              <input
                                type="checkbox"
                                checked={entry.status === "eligible"}
                                onChange={() => {
                                  setStudyEligibility((prev) => {
                                    const current = prev[studyItem.id]?.status;
                                    if (current === "eligible") {
                                      const copy = { ...prev };
                                      delete copy[studyItem.id];
                                      return copy;
                                    }
                                    return {
                                      ...prev,
                                      [studyItem.id]: { status: "eligible" },
                                    };
                                  });
                                }}
                                className="w-4 h-4 accent-emerald-500"
                              />
                            </div>
                            <div className="w-28 sm:w-60 flex flex-col items-center gap-1.5">
                              <input
                                type="checkbox"
                                checked={entry.status === "notEligible"}
                                onChange={() => {
                                  setStudyEligibility((prev) => {
                                    const current = prev[studyItem.id]?.status;
                                    if (current === "notEligible") {
                                      const copy = { ...prev };
                                      delete copy[studyItem.id];
                                      return copy;
                                    }
                                    return {
                                      ...prev,
                                      [studyItem.id]: {
                                        status: "notEligible",
                                        reason: "",
                                      },
                                    };
                                  });
                                }}
                                className="w-4 h-4 accent-rose-500 mt-1"
                              />
                              {/* In its row, in a wider column (2026-10-26; display
                                  only: same value, same save). Resizable. */}
                              {entry.status === "notEligible" && (
                                <textarea
                                  placeholder="Why Not Eligible?"
                                  value={entry.reason || ""}
                                  onChange={(e) => {
                                    setStudyEligibility((prev) => ({
                                      ...prev,
                                      [studyItem.id]: {
                                        ...prev[studyItem.id],
                                        reason: e.target.value,
                                      },
                                    }));
                                  }}
                                  rows={2}
                                  className={`${inputCls} w-full resize-y min-h-[60px] !text-[13px] leading-5`}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Screening Visit requirements for each study marked Eligible.
                    The list is what an admin confirmed on the study page from
                    the protocol's Schedule of Assessments — reference only, so
                    the coordinator knows what the screening visit needs. */}
                {activeStudies
                  .filter(
                    (s) =>
                      studyEligibility[s.id]?.status === "eligible" &&
                      hasConfirmedScreening(s)
                  )
                  .map((s) => (
                    <div
                      key={`screening-${s.id}`}
                      className="rounded-xl border border-emerald-400/20 bg-emerald-500/[0.05] p-3"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <ClipboardList className="w-3.5 h-3.5 text-emerald-300" />
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-200">
                            Screening Visit
                          </div>
                          <div className="text-xs text-slate-200 truncate">
                            {s.NAME}
                            {s.screeningVisit.visitLabel ? (
                              <span className="text-slate-400"> · SoA column “{s.screeningVisit.visitLabel}”</span>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {/* Needs health ride — the one item on this panel the
                          coordinator actually SETS. Amber, and above the rule,
                          because it is a logistics flag they own rather than a
                          protocol requirement read off the SoA. Stored per
                          study on the eligibility entry, so it rides along with
                          the existing save and audit trail and disappears if
                          the patient stops being eligible. */}
                      <label className="mb-2 flex items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-500/15 px-2.5 py-1.5 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={Boolean(
                            studyEligibility[s.id]?.needsHealthRide
                          )}
                          onChange={(e) => {
                            const { checked } = e.target;
                            setStudyEligibility((prev) => ({
                              ...prev,
                              [s.id]: {
                                ...prev[s.id],
                                needsHealthRide: checked,
                              },
                            }));
                          }}
                          className="w-3.5 h-3.5 accent-amber-500 shrink-0"
                        />
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                        <span className="text-xs text-amber-100">
                          Needs health ride
                        </span>
                      </label>

                      <div className="mb-2 h-px bg-emerald-400/15" />

                      {/* A confirmed EMPTY checklist is a real answer, shown
                          as such so it never reads like one nobody has done. */}
                      {!(s.screeningVisit.items || []).length && (
                        <p className="text-xs text-slate-200">
                          Nothing needs to be done at the hospital.
                        </p>
                      )}
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {(s.screeningVisit.items || []).map((item) => (
                          <li
                            key={item.id}
                            className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5"
                          >
                            <span className="mt-0.5 inline-flex w-3.5 h-3.5 rounded border border-emerald-300/50 shrink-0" aria-hidden="true" />
                            <div className="min-w-0">
                              <div className="text-xs text-slate-100 leading-snug">{item.label}</div>
                              {item.footnotes?.length > 0 && (
                                <div className="text-[10px] text-slate-400 leading-snug mt-0.5">
                                  {item.footnotes.join(" ")}
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </div>
            </Section>
          )}

          {/* Notes & Study */}
          <Section
            id="sec-notes"
            title="Notes & Study"
            subtitle="Free-text fields"
            icon={<FileText className="w-3.5 h-3.5 text-purple-300" />}
            defaultOpen={false}
          >
            <div className="space-y-3">
              <label className="block">
                <span className={labelCls}>Study</span>
                <input
                  type="text"
                  className={inputCls}
                  value={study}
                  onChange={(e) => setStudy(e.target.value)}
                  placeholder="Enter study name"
                />
              </label>
              <div>
                {/* Bigger and easier to read, with a read-only pop-up
                    (2026-10-26). Display only: the notes and who can edit
                    them are unchanged. */}
                <div className="flex items-center justify-between gap-2">
                  <span className={labelCls}>Legacy Notes</span>
                  {String(notes || "").trim() && (
                    <button
                      type="button"
                      onClick={() => setNotesViewer("legacy")}
                      className="mb-1 inline-flex items-center gap-1 px-2 py-1 rounded-lg border text-[11px] font-semibold transition notes-open-btn"
                    >
                      <Maximize2 className="w-3 h-3" />
                      Open larger
                    </button>
                  )}
                </div>
                <textarea
                  aria-label="Legacy Notes"
                  className={`${inputCls} resize-y min-h-[180px] !text-[14px] leading-6 ${
                    legacyNotesReadOnly
                      ? "opacity-75 cursor-not-allowed bg-white/[0.025]"
                      : ""
                  }`}
                  rows={Math.min(18, Math.max(7, String(notes || "").split("\n").length + 1))}
                  value={notes}
                  readOnly={legacyNotesReadOnly}
                  onChange={(e) => {
                    if (!legacyNotesReadOnly) setNotes(e.target.value);
                  }}
                  placeholder="Explain reason for screen failure, not interested, etc."
                />
                {isCoordinator && (
                  <p className="mt-1 text-[10px] text-slate-400">
                    Legacy notes are read-only for coordinators. Add new notes
                    in the coordinator note timeline below.
                  </p>
                )}
              </div>
            </div>
            <NotesViewerModal
              open={notesViewer === "legacy"}
              title="Legacy Notes"
              subtitle={[
                fullName,
                legacyNotesReadOnly ? "Read only" : "Read only here; edit them in the box on the page",
              ]
                .filter(Boolean)
                .join(" · ")}
              onClose={() => setNotesViewer(null)}
            >
              <p className="whitespace-pre-wrap break-words text-[15px] leading-7 notes-viewer-text">{notes}</p>
            </NotesViewerModal>
          </Section>

          {/* Coordinator Notes */}
          <Section
            id="sec-coordinator-notes"
            title="Coordinator Notes"
            subtitle="Append-only note timeline"
            icon={<ClipboardList className="w-3.5 h-3.5 text-purple-300" />}
            defaultOpen={false}
          >
            <PatientNotesTimeline
              patientId={token}
              patientName={fullName}
              mrnLast4={getMrnLast4(mrn)}
              source={source}
              actorRole={currentRole}
              canAddNote={canAddCoordinatorNote && !roleLoading}
              noteDraft={coordinatorNoteDraft}
              onNoteDraftChange={setCoordinatorNoteDraft}
            />
          </Section>

          {/* Files */}
          <Section
            id="sec-files"
            title="Files"
            subtitle="Upload consent / supporting documents"
            icon={<Upload className="w-3.5 h-3.5 text-purple-300" />}
            defaultOpen={false}
            headerExtra={
              fileUrl || (surveyView && (surveyView.open.length || surveyView.attachNeeded || surveyView.state === "texted")) ? (
                <>
                  {fileUrl ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-300/30 text-[10px] font-semibold text-emerald-200">
                      <CheckCircle2 className="w-3 h-3" />
                      Uploaded
                    </span>
                  ) : null}
                  {surveyView && surveyView.open.length > 0 ? (
                    <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-semibold patient-survey-chip-check">
                      {surveyView.open.length} survey answer{surveyView.open.length === 1 ? "" : "s"} to check
                    </span>
                  ) : surveyView && surveyView.attachNeeded ? (
                    <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-semibold patient-survey-chip-check">
                      Survey answers to attach
                    </span>
                  ) : surveyView && surveyView.state === "texted" ? (
                    <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-semibold pft-survey-chip-texted">
                      Survey texted
                    </span>
                  ) : null}
                </>
              ) : null
            }
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className={labelCls}>Upload File</span>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className={`${inputCls} file:mr-3 file:px-3 file:py-1 file:rounded-md file:border file:border-white/10 file:bg-purple-500/15 file:text-purple-200 file:text-xs file:font-medium file:cursor-pointer hover:file:bg-purple-500/25 cursor-pointer`}
                  onChange={handleFileUpload}
                  disabled={uploading}
                />
                {uploading && (
                  <p className="mt-2 text-xs text-slate-400 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Uploading…
                  </p>
                )}
              </label>
              <div className="flex items-end">
                {fileUrl ? (
                  <a
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-purple-300/30 text-xs font-medium text-slate-200 hover:text-white transition"
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                    View Current File
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="text-xs text-slate-500 italic">
                    No file uploaded yet.
                  </span>
                )}
              </div>
            </div>
            {otherFiles.length > 0 && (
              <div className="mt-3">
                <span className={labelCls}>Other files</span>
                <ul className="space-y-1.5">
                  {otherFiles.map((f, i) => (
                    <li key={`${f.url}-${i}`} className="flex flex-wrap items-center gap-2 text-xs text-slate-300">
                      <a
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-purple-300/30 text-xs font-medium text-slate-200 hover:text-white transition"
                      >
                        <Paperclip className="w-3.5 h-3.5" />
                        {f.name || "File"}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                      {f.addedBy && <span className="text-slate-500">added by {f.addedBy}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {surveyPatient && (
              <PatientSurveyPanel
                patient={surveyPatient}
                survey={patientSurvey}
                hasFile={!!fileUrl}
                current={{ phone, physicalAddress, email }}
                submissionIds={surveySubmissionIds}
              />
            )}
          </Section>
        </div>

        {/* Summary panel */}
        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-[64px] space-y-3 max-h-[calc(100vh-80px)] overflow-y-auto scrollbar-thin">
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden">
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-80 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.05] via-transparent to-blue-500/[0.05] pointer-events-none" />
              <div className="relative p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-sm font-bold text-white ring-1 ring-white/20 shrink-0">
                    {(fullName || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[9px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold leading-none">
                      Summary
                    </div>
                    <div className="text-sm font-semibold text-white truncate">
                      {fullName || "Unnamed Patient"}
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5 text-[11px]">
                  <div className="flex items-center justify-between px-2 py-1 rounded-md bg-white/[0.03] border border-white/5">
                    <span className="text-slate-400 inline-flex items-center gap-1.5">
                      <IdCard className="w-3 h-3 text-purple-300" />
                      MRN
                    </span>
                    <span className="font-mono text-slate-200">{mrn || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between px-2 py-1 rounded-md bg-white/[0.03] border border-white/5">
                    <span className="text-slate-400 inline-flex items-center gap-1.5">
                      <Calendar className="w-3 h-3 text-purple-300" />
                      DOB
                    </span>
                    <span className="text-slate-200">{dob || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between px-2 py-1 rounded-md bg-white/[0.03] border border-white/5">
                    <span className="text-slate-400 inline-flex items-center gap-1.5">
                      <Activity className="w-3 h-3 text-purple-300" />
                      Status
                    </span>
                    <Chip tone={inDuplicates ? "danger" : statusTone(status)}>
                      {inDuplicates
                        ? "Potential Duplicate"
                        : statusLabel(status) || "Unspecified"}
                    </Chip>
                  </div>
                  {diagBadge.show && (
                    <div className="flex items-center justify-between gap-2 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-400/30">
                      <span className="text-emerald-200 inline-flex items-center gap-1.5 shrink-0">
                        <Stethoscope className="w-3 h-3" />
                        Enrolled in Diagnostics
                      </span>
                      <span className="text-[10px] text-emerald-200 truncate">
                        {diagBadge.legacy ? "no study recorded" : diagStudyNames}
                      </span>
                    </div>
                  )}
                </div>

                {isUrgent && (
                  <div className="rounded-lg border border-rose-400/30 bg-rose-500/10 px-2 py-1.5 flex items-center gap-1.5 shadow-[0_0_15px_-4px_rgba(244,63,94,0.5)]">
                    <Flame className="w-3.5 h-3.5 text-rose-300 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-wider text-rose-200 font-semibold leading-none">
                        Urgent
                      </div>
                      {urgentReason && (
                        <div className="text-[11px] text-rose-100/90 truncate">
                          {urgentReason}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
                    Diagnosis
                  </div>
                  {diagnosis.length === 0 ? (
                    <span className="text-xs text-slate-500 italic">
                      None selected
                    </span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {diagnosis.map((d) => (
                        <span
                          key={d}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-500/15 border border-purple-300/30 text-purple-200"
                        >
                          <HeartPulse className="w-2.5 h-2.5" />
                          {diagnosisLabel(d)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-1.5">
                  <div className="rounded-lg bg-white/[0.03] border border-white/5 px-2 py-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-slate-400">
                      Studies
                    </div>
                    <div className="text-base font-bold text-purple-200">
                      {potentialStudyIds.length}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white/[0.03] border border-white/5 px-2 py-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-slate-400">
                      Active
                    </div>
                    <div className="text-base font-bold text-emerald-200">
                      {activeStudies.length}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white/[0.03] border border-white/5 px-2 py-1.5">
                    <div className="text-[9px] uppercase tracking-wider text-slate-400">
                      File
                    </div>
                    <div className="text-base font-bold text-white">
                      {fileUrl ? "✓" : "—"}
                    </div>
                  </div>
                </div>

                <div className="relative group/savewarn2">
                  {/* Warning tooltip — appears on hover above the sidebar save button */}
                  <div className="absolute bottom-full left-0 right-0 mb-2 pointer-events-none opacity-0 group-hover/savewarn2:opacity-100 transition-opacity duration-200 z-50">
                    <div className="save-return-warning flex items-start gap-1.5 px-2.5 py-2 rounded-lg bg-rose-600 border border-rose-400/60 text-[10px] text-white font-medium leading-snug shadow-[0_4px_20px_-4px_rgba(244,63,94,0.6)]">
                      <AlertTriangle className="save-return-warning-icon w-3 h-3 mt-0.5 shrink-0 text-rose-200" />
                      <span>This button does not save coordinator notes. Remember to save your notes separately before leaving.</span>
                    </div>
                    <div className="save-return-warning-arrow absolute -bottom-1 left-5 w-2 h-2 bg-rose-600 border-r border-b border-rose-400/60 rotate-45" />
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="group relative w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    {saving ? (
                      <Loader2 className="w-4 h-4 relative animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 relative" />
                    )}
                    <span className="relative">
                      {saving ? "Saving…" : "Save & Return"}
                    </span>
                  </button>
                </div>
                <button
                  onClick={handleBack}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs font-medium text-slate-200 hover:text-white transition"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Cancel
                </button>
              </div>
            </div>

            {/* Patient Audit Trail — read-only timeline */}
            <PatientAuditTimeline patientId={token} />
          </div>
        </aside>
      </div>

      {/* Which study? — shown when Enrolled is picked or "Add study" is clicked */}
      <EnrollStudyPickerModal
        open={showEnrollPicker}
        studies={studyDocs}
        studiesById={studiesById}
        currentEntries={splitCurrentAndPast(studyEnrollments, studiesById).current}
        onCancel={() => setShowEnrollPicker(false)}
        onConfirm={handleEnrollConfirm}
      />

      {/* Unsaved coordinator notes blocking modal */}
      <AnimatePresence>
        {showUnsavedNotesModal && (
          <motion.div
            key="unsaved-notes-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0, y: 8 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="unsaved-notes-modal relative w-full max-w-sm rounded-2xl border border-rose-400/40 bg-slate-950/95 backdrop-blur-2xl shadow-[0_24px_60px_-12px_rgba(244,63,94,0.5)] overflow-hidden"
            >
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-rose-400 to-orange-400 opacity-80 pointer-events-none" />
              <div className="p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-rose-500/20 border border-rose-400/30 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-4.5 h-4.5 text-rose-300 w-[18px] h-[18px]" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white leading-tight">
                      Unsaved Coordinator Notes
                    </h3>
                    <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                      You have typed notes that have not been saved yet. Please scroll down to the{" "}
                      <span className="font-semibold text-white">Coordinator Notes</span> section and click{" "}
                      <span className="font-semibold text-emerald-300">Save Note</span> before leaving — otherwise your notes will be lost.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowUnsavedNotesModal(false)}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-orange-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(244,63,94,0.6)] active:scale-[0.98] transition-all"
                >
                  OK, I'll Save My Notes First
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Admin MRN edit: another patient already has this MRN (warn, never block) */}
      <AnimatePresence>
        {mrnDupPrompt && (
          <motion.div
            key="mrn-dup-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label="This MRN is already in RegX"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0, y: 8 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="mrn-dup-modal relative w-full max-w-sm rounded-2xl border border-amber-400/40 bg-slate-950/95 backdrop-blur-2xl shadow-[0_24px_60px_-12px_rgba(245,158,11,0.45)] overflow-hidden"
            >
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-amber-400 to-orange-400 opacity-80 pointer-events-none" />
              <div className="p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-[18px] h-[18px] text-amber-300" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white leading-tight">This MRN is already in RegX</h3>
                    <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                      {mrnDupPrompt.matches.length === 1 ? "Another patient has" : "Other patients have"} the MRN you
                      entered. Check it's right before saving.
                    </p>
                    <ul className="mt-2 space-y-1">
                      {mrnDupPrompt.matches.slice(0, 5).map((m) => (
                        <li key={`${m.source}:${m.id}`} className="text-xs text-slate-200">
                          <span className="font-semibold text-white">{m.fullName || m.name || "No name"}</span>
                          {" · "}
                          {m.source === "duplicates" ? "Potential Duplicates" : statusLabel(m.status) || "No box"}
                        </li>
                      ))}
                      {mrnDupPrompt.matches.length > 5 && (
                        <li className="text-xs text-slate-400">and {mrnDupPrompt.matches.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                </div>
                <div className="grid gap-2">
                  <button
                    type="button"
                    onClick={() => mrnDupPrompt.resolve(false)}
                    className="mrn-dup-cancel w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-sm font-semibold text-white active:scale-[0.98] transition-all"
                  >
                    Cancel, don't save
                  </button>
                  <button
                    type="button"
                    onClick={() => mrnDupPrompt.resolve(true)}
                    className="mrn-dup-save w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs font-medium text-slate-200 transition"
                  >
                    Save anyway
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Unsaved changes: leaving by Back / Cancel asks first */}
      <AnimatePresence>
        {showLeaveModal && (
          <motion.div
            key="unsaved-changes-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-label="Unsaved changes"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0, y: 8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.94, opacity: 0, y: 8 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="unsaved-changes-modal relative w-full max-w-sm rounded-2xl border border-amber-400/40 bg-slate-950/95 backdrop-blur-2xl shadow-[0_24px_60px_-12px_rgba(245,158,11,0.45)] overflow-hidden"
            >
              <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-amber-400 to-orange-400 opacity-80 pointer-events-none" />
              <div className="p-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-400/30 flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-[18px] h-[18px] text-amber-300" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white leading-tight">Unsaved changes</h3>
                    <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                      You've made changes to this patient that haven't been saved
                      {coordinatorNoteDraft.trim() ? ", including a coordinator note" : ""}. If you leave now, they'll be lost.
                    </p>
                  </div>
                </div>
                <div className="grid gap-2">
                  <button
                    type="button"
                    onClick={() => setShowLeaveModal(false)}
                    className="unsaved-changes-keep w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-sm font-semibold text-white active:scale-[0.98] transition-all"
                  >
                    Keep editing
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowLeaveModal(false);
                      goBackToOrigin();
                    }}
                    className="unsaved-changes-leave w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs font-medium text-slate-200 transition"
                  >
                    Leave without saving
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile sticky bottom save bar */}
      <div className="lg:hidden fixed bottom-3 inset-x-3 z-30">
        <div className="rounded-2xl border border-white/10 bg-slate-950/80 backdrop-blur-2xl shadow-[0_20px_60px_-15px_rgba(124,58,237,0.6)] p-1.5 flex items-center gap-1.5">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs font-medium text-slate-200 hover:text-white transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </button>
          {isUrgent && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-rose-500/20 border border-rose-400/40 text-[10px] font-bold text-rose-100">
              <Flame className="w-3 h-3" />
              URGENT
            </span>
          )}
          <span className="ml-auto" />
          <button
            onClick={handleSave}
            disabled={saving}
            className="group relative inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 relative animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5 relative" />
            )}
            <span className="relative">
              {saving ? "Saving…" : "Save & Return"}
            </span>
          </button>
        </div>
      </div>

      {/* Research-calendar scheduling modal */}
      <ScheduleVisitModal
        open={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        patientName={fullName}
        patientToken={token}
        appt={{
          date: dateScheduled,
          startTime: apptStartTime,
          endTime: apptEndTime,
          room: apptRoom,
          otherRoom: apptOtherRoom,
          coordinator: apptCoordinator,
          coordinatorEmail: apptCoordinatorEmail,
          study: apptStudy || study,
          visit: apptVisit,
          createdBy: apptCreatedBy,
          eventId: calendarEventId,
          eventLink: calendarEventLink,
        }}
        onSaved={(a) => {
          setDateScheduled(a.date);
          setApptStartTime(a.startTime);
          setApptEndTime(a.endTime);
          setApptRoom(a.room);
          setApptOtherRoom(a.otherRoom);
          setApptCoordinator(a.coordinator);
          setApptCoordinatorEmail(a.coordinatorEmail);
          setApptStudy(a.study);
          setApptVisit(a.visit);
          setApptCreatedBy(a.createdBy);
          setCalendarEventId(a.eventId);
          setCalendarEventLink(a.eventLink);
          setToast({
            type: "success",
            msg: "Added to Research calendar — click Save to keep it on the record.",
          });
        }}
        onRemoved={() => {
          setCalendarEventId("");
          setCalendarEventLink("");
          setToast({
            type: "warn",
            msg: "Removed from Research calendar — click Save to update the record.",
          });
        }}
      />
    </div>
    </SectionExpandContext.Provider>
  );
};

export default PatientDetailPage;
